import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import * as api from "../api";
import { useToast } from "../components/Toast";
import Modal, { ModalActions } from "../components/Modal";
import FormulaModal from "../components/FormulaModal";
import { PlusIcon, CheckIcon, XIcon, TrashIcon, SettingsIcon, EditIcon } from "../components/Icons";
import DownloadMenu from "../components/DownloadMenu";

/* ─── Status helpers ─── */
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Internal-only assumption key controlling whether the user enters
// Occupancy values or Rooms Sold values for Actual / Budgeted.
const OCCUPANCY_MODE_KEY = "__mode__occupancy_input";

// Per-row unit hint + the small symbol shown inside each cell.
//   actual/projected:   prose unit shown below the row name
//   actualSym/projSym:  visual symbol shown inside the cell (after the value)
const UNIT_HINT = {
  "Occupancy Growth %": { actual: "%",            actualSym: "%",  projected: "%", projSym: "%" },
  "Rooms Sold":         { actual: "rooms",        actualSym: "",   projected: "%", projSym: "%" },
  "ARR Growth %":       { actual: "₹ per room",   actualSym: "₹",  projected: "%", projSym: "%" },
  "F&B Revenue % of Room Revenue":  { actual: "₹ Cr",  actualSym: "Cr", projected: "%", projSym: "%" },
  "OOD Revenue % of Room Revenue":  { actual: "₹ Cr",  actualSym: "Cr", projected: "%", projSym: "%" },
  "Operating Expenses":             { actual: "₹ Cr",  actualSym: "Cr", projected: "₹ Cr", projSym: "Cr" },
  "Corporate Expenses":             { actual: "₹ Cr",  actualSym: "Cr", projected: "₹ Cr", projSym: "Cr" },
  "Depreciation":                   { actual: "₹ Cr",  actualSym: "Cr", projected: "₹ Cr", projSym: "Cr" },
  "Finance Cost":                   { actual: "₹ Cr",  actualSym: "Cr", projected: "₹ Cr", projSym: "Cr" },
  "Secured Loan Interest Rate":     { actual: "% per annum", actualSym: "%", projected: "% per annum", projSym: "%" },
  "Unsecured Loan Interest Rate":   { actual: "% per annum", actualSym: "%", projected: "% per annum", projSym: "%" },
};

// Display label for an assumption row. Storage keys keep their original
// names (incl. "%") so we don't break existing data.
const rowDisplayName = (k) =>
  k === "Occupancy Growth %" ? "Occupancy Growth" : k;

// Sections in the Assumptions tab — used for collapse-all.
const ASSUMPTION_SECTIONS = ["status", "breakdown", "interest", "otherInputs", "assumptions"];

// Fallback list shown before /api/variable-categories has loaded. The real
// category order comes from the user's drag-arranged list in Settings →
// Variable Templates → Categories.
const FALLBACK_CATEGORIES = [
  "Profit & Loss Statement",
  "Cash Flow Statement",
  "Loan Schedule (Secured + Unsecured)",
  "Capital Structure",
  "Unit Economics",
];
// Short labels for narrow tab strips.
const CATEGORY_TAB_LABEL = {
  "Profit & Loss Statement": "P&L Statement",
  "Cash Flow Statement": "Cash Flow",
  "Loan Schedule (Secured + Unsecured)": "Loan Schedule",
  "Capital Structure": "Capital Structure",
  "Unit Economics": "Unit Economics",
};

// Static mapping from key name → breakdown source for the Actual-year auto-fill
// in the Other Inputs section. Only keys listed here get an auto Actual cell;
// every other key (including user-added ones) uses manual entry across all years.
const OTHER_INPUT_AUTO_MAP = {
  "Operating Expenses": "operating",
  "Corporate Expenses": "corporate",
};

// Reusable collapsible card. Header is always visible and clickable;
// body shows only when collapsed === false. The actions slot lives in the
// header right-edge for things like "Add" buttons.
function CollapsibleCard({ collapsed, onToggle, title, subtitle, actions, children }) {
  return (
    <div className="card" style={{ padding: collapsed ? "14px 22px" : 22 }}>
      <div className="flex justify-between items-center" style={{ gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onToggle}
          style={{
            background: "none", border: "none", padding: 0, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 10, color: "var(--text-primary)",
            textAlign: "left", flex: 1, minWidth: 0,
          }}
        >
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2"
            style={{ transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s ease", flexShrink: 0 }}>
            <path d="M6 9l6 6 6-6" />
          </svg>
          <span style={{ minWidth: 0 }}>
            <span className="heading fw-600" style={{ fontSize: 17 }}>{title}</span>
            {subtitle && <span className="text-xs text-muted" style={{ display: "block", marginTop: 4, fontWeight: 400 }}>{subtitle}</span>}
          </span>
        </button>
        {actions && <div onClick={(e) => e.stopPropagation()}>{actions}</div>}
      </div>
      {!collapsed && <div style={{ marginTop: 16 }}>{children}</div>}
    </div>
  );
}

// Convert "2026-03" (HTML <input type="month">) → "Mar'26"
function monthInputToLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const mi = parseInt(m, 10) - 1;
  if (!MONTH_NAMES[mi]) return ym;
  return `${MONTH_NAMES[mi]}'${y.slice(-2)}`;
}

// Convert "Mar'26" or "mar'26" → "2026-03" for pre-filling the input
function labelToMonthInput(label) {
  if (!label) return "";
  const m = label.trim().match(/^([A-Za-z]{3,})['\s-]?(\d{2,4})$/);
  if (!m) return "";
  const monIdx = MONTH_NAMES.findIndex((n) => n.toLowerCase() === m[1].slice(0, 3).toLowerCase());
  if (monIdx < 0) return "";
  let yr = m[2];
  if (yr.length === 2) yr = "20" + yr;
  return `${yr}-${String(monIdx + 1).padStart(2, "0")}`;
}

// Tiny corner indicator drawn in the top-left of a <td> to flag whether
// the cell's value is auto-computed (⚡) or manually entered (pencil).
// Renders nothing for cells that are neither (e.g. historical snapshots).
// Used across AssumptionsTab so the cell-source convention matches the
// statement-tab icon vocabulary (variable rows / formula-driven values).
function CellSourceBadge({ kind, title }) {
  if (kind !== "auto" && kind !== "manual") return null;
  const isAuto = kind === "auto";
  return (
    <span
      title={title || (isAuto ? "Auto-calculated value" : "Manual entry expected")}
      style={{
        position: "absolute", top: 2, left: 4,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: isAuto ? "var(--accent)" : "var(--orange-light)",
        opacity: 0.85, pointerEvents: "none",
      }}
    >
      {isAuto ? (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
        </svg>
      ) : (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      )}
    </span>
  );
}

/* ─── Project Assumptions Tab ─── */
function AssumptionsTab({ projectId, canEdit, isAdmin = false, showHistorical = false, onChange }) {
  // `onChange` fires after every successful Input Data mutation so the
  // parent can refetch the project — keeps P&L / Cash Flow / Loan Schedule
  // in sync with edits made here without the user switching tabs to refresh.
  const notifyChange = () => { try { onChange && onChange(); } catch {} };
  // ── Assumption-key management modals (Admin-only) ──
  // editKeyModal: { mode: "add" | "edit", id?, key, section, year_wise, default_value }
  const [editKeyModal, setEditKeyModal] = useState(null);
  // formulaModal: { keyName, formula }
  const [formulaModal, setFormulaModal] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});         // { "key|year" : value }
  const [saving, setSaving] = useState(false);
  const [showAddSub, setShowAddSub] = useState(false);
  const [newSub, setNewSub] = useState({ name: "", status: "Operational", keys: 0, operational_days: 365 });
  const [editingSub, setEditingSub] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [editingLineItem, setEditingLineItem] = useState(null);  // { id, name, value }
  const [newLineItem, setNewLineItem] = useState({ category: null, name: "", value: 0 });
  // Section collapse state — default all collapsed.
  const [collapsed, setCollapsed] = useState(() =>
    Object.fromEntries(ASSUMPTION_SECTIONS.map((s) => [s, true]))
  );
  // Per-cell input mode for Occupancy Growth row Actual/Budgeted cells.
  // Auto-defaults from existing data; user can flip per cell.
  const [occCellMode, setOccCellMode] = useState({}); // { 2025: "pct" | "rooms" }
  const toast = useToast();
  const toggleCollapsed = (key) => setCollapsed((p) => ({ ...p, [key]: !p[key] }));
  const expandAll = () => setCollapsed(Object.fromEntries(ASSUMPTION_SECTIONS.map((s) => [s, false])));
  const collapseAll = () => setCollapsed(Object.fromEntries(ASSUMPTION_SECTIONS.map((s) => [s, true])));

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.getProjectAssumptions(projectId),
      api.getProjectLineItems(projectId).catch(() => ({ items: [] })),
    ])
      .then(([a, li]) => {
        setData(a);
        setLineItems(li.items || []);
      })
      .catch(() => toast("Failed to load assumptions", "error"))
      .finally(() => setLoading(false));
  }, [projectId, toast]);

  useEffect(() => { load(); }, [load]);

  // Hooks below MUST run on every render (rules of hooks). They handle the
  // null-data case internally so the early-return spinner can come after.
  const formulaRefNames = useMemo(() => {
    const names = new Set();
    // 1. Assumption keys from the Input Data section itself.
    (data?.default_keys || []).forEach((k) => names.add(k.key));
    // 2. Canonical synthesized P&L variables.
    [
      "Total Room Count", "Rooms Sold", "Occupancy %", "ARR",
      "No. of Days of Operation", "Room Revenue", "F&B Revenue",
      "OOD Revenue", "Total Revenue", "Operating Expenses",
      "Corporate Expenses", "Depreciation", "EBIDTA", "PBT", "PAT",
      "Operating Expenses %", "Capex Total",
    ].forEach((n) => names.add(n));
    // 3. Every Variable Template name — keeps the Input Data formula chip
    // palette in sync with the one in Settings → Variable Templates, so a
    // formula defined on a template variable is referenceable here too.
    (data?.template_names || []).forEach((n) => names.add(n));
    return Array.from(names);
  }, [data]);

  if (loading || !data) return <div className="loading-container"><div className="spinner" /></div>;

  const { sub_units, assumptions, default_keys } = data;
  const projectActualYear = data.actual_year;
  // Hide historical year columns by default — toggle from project header.
  const allYears = data.years || [];
  const years = showHistorical
    ? allYears
    : allYears.filter((y) => parseInt(y) >= projectActualYear);
  const yearWiseKeys = default_keys.filter((k) => k.year_wise).map((k) => k.key);
  const globalKeys = default_keys.filter((k) => !k.year_wise).map((k) => k.key);

  // Tag a year as historical / actual / budgeted / projected based on the
  // project's current Actual FY. Drives the column header coloring + the
  // editability rules in this tab.
  const yearKind = (y) => {
    const yr = parseInt(y);
    if (isNaN(yr)) return "projected";
    if (yr < projectActualYear) return "historical";
    if (yr === projectActualYear) return "actual";
    if (yr === projectActualYear + 1) return "budgeted";
    return "projected";
  };

  // Lookup the raw key descriptor (id, formula, section) by name.
  const keyMeta = {};
  default_keys.forEach((k) => { keyMeta[k.key] = k; });
  const keysInSection = (section) =>
    default_keys.filter((k) => (k.section || "assumptions") === section);

  // ── Assumption-key CRUD handlers ──
  const openAddKeyModal = (presetSection = "assumptions") => {
    setEditKeyModal({
      mode: "add", id: null,
      key: "", section: presetSection, year_wise: presetSection !== "rate_of_interest",
      default_value: 0,
    });
  };
  const openEditKeyModal = (keyDesc) => {
    setEditKeyModal({
      mode: "edit", id: keyDesc.id,
      key: keyDesc.key,
      section: keyDesc.section || "assumptions",
      year_wise: !!keyDesc.year_wise,
      default_value: keyDesc.default_value || 0,
    });
  };
  const handleSaveKeyModal = async () => {
    if (!editKeyModal) return;
    const payload = {
      key: editKeyModal.key.trim(),
      section: editKeyModal.section,
      year_wise: !!editKeyModal.year_wise,
      default_value: parseFloat(editKeyModal.default_value) || 0,
    };
    if (!payload.key) return;
    try {
      if (editKeyModal.mode === "add") {
        await api.addAssumptionKey(payload);
        toast(`Input variable "${payload.key}" added`);
      } else {
        await api.updateAssumptionKey(editKeyModal.id, payload);
        toast("Input variable updated");
      }
      setEditKeyModal(null);
      load();
      notifyChange();
    } catch (e) { toast(e.message, "error"); }
  };
  const handleDeleteKey = async (keyDesc) => {
    if (!keyDesc?.id) return;
    if (!window.confirm(`Delete input variable "${keyDesc.key}"? This removes its rows from every project.`)) return;
    try {
      await api.deleteAssumptionKey(keyDesc.id);
      toast(`Input variable "${keyDesc.key}" deleted`);
      load();
      notifyChange();
    } catch (e) { toast(e.message, "error"); }
  };
  const handleSaveFormula = async (newFormula) => {
    if (!formulaModal) return;
    const expr = (newFormula || "").trim();
    const existingId = formulaModal.formula?.id;
    try {
      if (expr) {
        if (existingId) {
          await api.updateFormula(existingId, {
            variable_name: formulaModal.keyName,
            formula_type: "custom",
            formula_expression: expr,
            description: "",
          });
        } else {
          await api.createFormula({
            variable_name: formulaModal.keyName,
            formula_type: "custom",
            formula_expression: expr,
            description: "",
          });
        }
      } else if (existingId) {
        await api.deleteFormula(existingId);
      }
      toast(expr ? "Formula saved" : "Formula cleared");
      setFormulaModal(null);
      load();
      notifyChange();
    } catch (e) { toast(e.message, "error"); }
  };
  // formulaRefNames computed above the early-return so its hook position is stable.

  // Build lookup: { "Occupancy Growth %": { 2027: 0.03, ... }, "Secured Loan Interest Rate": { null: 0.085 } }
  const lookup = {};
  assumptions.forEach((a) => {
    if (!lookup[a.key]) lookup[a.key] = {};
    lookup[a.key][a.year == null ? "all" : a.year] = a.value;
  });

  const draftKey = (k, y) => `${k}|${y == null ? "all" : y}`;
  const getValue = (k, y) => {
    const dk = draftKey(k, y);
    if (dk in draft) return draft[dk];
    const v = lookup[k]?.[y == null ? "all" : y];
    return v == null ? "" : v;
  };

  const setDraftValue = (k, y, v) => setDraft((prev) => ({ ...prev, [draftKey(k, y)]: v }));

  // Per-cell occupancy input mode. Default = "rooms" per product spec; if
  // the cell *has* a non-zero Occupancy Growth % value we keep showing %
  // so existing data isn't hidden.
  const detectOccMode = (year) => {
    const rs = assumptions.find((a) => a.key === "Rooms Sold" && a.year === year)?.value;
    if (rs) return "rooms";
    const pct = assumptions.find((a) => a.key === "Occupancy Growth %" && a.year === year)?.value;
    return pct ? "pct" : "rooms";
  };
  const getOccMode = (year) => occCellMode[year] || detectOccMode(year);
  const setOccMode = (year, mode) => {
    setOccCellMode((p) => ({ ...p, [year]: mode }));
    // Clear the inactive value so backend picks the right one and avoids stale data.
    if (mode === "pct") setDraftValue("Rooms Sold", year, 0);
    else setDraftValue("Occupancy Growth %", year, 0);
  };

  const handleSave = async () => {
    const payload = Object.entries(draft).map(([dk, v]) => {
      const [key, yr] = dk.split("|");
      return { key, year: yr === "all" ? null : parseInt(yr), value: parseFloat(v) || 0 };
    });
    if (payload.length === 0) { toast("No changes to save"); return; }
    setSaving(true);
    try {
      await api.updateProjectAssumptions(projectId, payload);
      toast(`Saved ${payload.length} assumption${payload.length === 1 ? "" : "s"}`);
      setDraft({});
      load();
    } catch (e) { toast(e.message, "error"); }
    setSaving(false);
  };

  const isPct = (k) => k.includes("%") || k.toLowerCase().includes("rate");

  // Per-cell unit/symbol lookup. yearIdx === null means a global value (Rate of Interest).
  const cellUnit = (k, yearIdx) => {
    const u = UNIT_HINT[k];
    if (!u) return "";
    if (yearIdx == null) return u.actual || "";
    return yearIdx <= 1 ? u.actual : u.projected;
  };
  const cellSymbol = (k, yearIdx) => {
    const u = UNIT_HINT[k];
    if (!u) return "";
    if (yearIdx == null) return u.actualSym || "";
    return yearIdx <= 1 ? (u.actualSym || "") : (u.projSym || "");
  };
  // A cell is treated as % (×100 / ÷100) when its unit prose contains "%".
  const treatAsPctCell = (k, yearIdx) => /%/.test(cellUnit(k, yearIdx) || "");

  const fmtCell = (k, v, yearIdx) => {
    if (v === "" || v == null) return "";
    if (treatAsPctCell(k, yearIdx)) return (parseFloat(v) * 100).toFixed(2);
    return parseFloat(v);
  };
  const parseCell = (k, raw, yearIdx) => {
    if (raw === "" || raw == null) return "";
    const n = parseFloat(raw);
    if (isNaN(n)) return "";
    return treatAsPctCell(k, yearIdx) ? n / 100 : n;
  };

  const totalKeys = sub_units.reduce((s, u) => s + (parseFloat(u.keys) || 0), 0);
  // Per-row "No. of Days" = keys × operational_days / total_keys (Excel: =L17*M17/L19).
  // Excel cells display this as integer, so round for display.
  const effectiveDaysRaw = (u) => {
    if (!totalKeys) return 0;
    const k = parseFloat(u.keys) || 0;
    const d = parseFloat(u.operational_days) || 0;
    return k * d / totalKeys;
  };
  const effectiveDays = (u) => Math.round(effectiveDaysRaw(u));
  // Weighted avg = Σ(keys × days) / Σ(keys) — sum the raw (unrounded) values
  // so the total matches Excel: =SUM(N17:N18) (e.g., 365 exactly, not 365.05).
  const weightedAvgDays = Math.round(sub_units.reduce((s, u) => s + effectiveDaysRaw(u), 0));

  // Total Room Count per fiscal year = sum of sub-unit keys whose Date Available
  // is on or before the start of that fiscal year (Indian FY = Apr-Mar).
  // "Operational" → counts in every year. A label like "Mar'26" → its fiscal
  // year is 2026; counts for FY > 2026 (i.e. FY27 onwards), matching the Excel.
  const goLiveFY = (status) => {
    const ym = labelToMonthInput(status);
    if (!ym) return null;
    const [y, m] = ym.split("-").map(Number);
    return m >= 4 ? y + 1 : y;
  };
  const totalRoomCountForYear = (y) => {
    const yr = parseInt(y);
    return sub_units.reduce((sum, u) => {
      const k = parseFloat(u.keys) || 0;
      if (u.status === "Operational") return sum + k;
      const fy = goLiveFY(u.status);
      return fy != null && fy < yr ? sum + k : sum;
    }, 0);
  };

  // ── Sub-unit handlers ──
  const handleAddSub = async () => {
    if (!newSub.name.trim()) return;
    try {
      await api.addProjectSubUnit(projectId, newSub);
      toast(`"${newSub.name}" added`);
      setShowAddSub(false);
      setNewSub({ name: "", status: "Operational", keys: 0, operational_days: 365 });
      load();
      notifyChange();
    } catch (e) { toast(e.message, "error"); }
  };

  const handleSaveSubEdit = async () => {
    if (!editingSub) return;
    try {
      await api.updateProjectSubUnit(projectId, editingSub.id, editingSub);
      toast("Sub-unit updated");
      setEditingSub(null);
      load();
      notifyChange();
    } catch (e) { toast(e.message, "error"); }
  };

  const handleDeleteSub = async (sid, name) => {
    try { await api.deleteProjectSubUnit(projectId, sid); toast(`"${name}" removed`); load(); notifyChange(); }
    catch (e) { toast(e.message, "error"); }
  };

  // ── Line item handlers (Other Income / Operating Expense breakdown) ──
  const handleAddLineItem = async (category) => {
    if (!newLineItem.name.trim()) return;
    try {
      await api.addProjectLineItem(projectId, {
        category, name: newLineItem.name.trim(),
        value: parseFloat(newLineItem.value) || 0,
        is_corporate: !!newLineItem.is_corporate,
      });
      toast("Line item added");
      setNewLineItem({ category: null, name: "", value: 0, is_corporate: false });
      load();
      notifyChange();
    } catch (e) { toast(e.message, "error"); }
  };
  const handleSaveLineItemEdit = async () => {
    if (!editingLineItem) return;
    try {
      await api.updateProjectLineItem(projectId, editingLineItem.id, {
        name: editingLineItem.name,
        value: parseFloat(editingLineItem.value) || 0,
        ...(editingLineItem.category ? { category: editingLineItem.category } : {}),
      });
      toast("Line item updated");
      setEditingLineItem(null);
      load();
      notifyChange();
    } catch (e) { toast(e.message, "error"); }
  };

  // Category options shown when editing a line item — lets the user move
  // an item between Income / Operating / Corporate / Budget panels.
  const LINE_ITEM_CATEGORIES = [
    { id: "other_income",      label: "Other Income" },
    { id: "operating_expense", label: "Operating Expenses" },
  ];
  const handleDeleteLineItem = async (lid) => {
    try { await api.deleteProjectLineItem(projectId, lid); toast("Line item removed"); load(); notifyChange(); }
    catch (e) { toast(e.message, "error"); }
  };

  const dirty = Object.keys(draft).length > 0;
  const fmtMoney = (n) => {
    const x = parseFloat(n) || 0;
    return x.toLocaleString("en-IN");
  };
  const incomeItems = lineItems.filter((li) => li.category === "other_income");
  // All operating-expense items live in one panel — including those flagged
  // as Corporate (legacy "corporate_expense" category, or is_corporate=true).
  // The Corporate badge per item drives Corporate Expenses Variable derivation.
  const isCorporateItem = (li) => !!li.is_corporate || li.category === "corporate_expense";
  const operatingItems = lineItems.filter((li) =>
    li.category === "expense" || li.category === "operating_expense" || li.category === "corporate_expense"
  );
  const budgetCapexItems = lineItems.filter((li) => li.category === "budget_capex");
  const budgetFinanceItems = lineItems.filter((li) => li.category === "budget_finance");
  const sumVals = (arr) => arr.reduce((s, it) => s + (parseFloat(it.value) || 0), 0);
  const incomeTotal = sumVals(incomeItems);
  const operatingTotalAll = sumVals(operatingItems);
  const corporateTotal = sumVals(operatingItems.filter(isCorporateItem));
  const operatingTotal = operatingTotalAll - corporateTotal; // non-corporate operating
  const budgetCapexTotal = sumVals(budgetCapexItems);
  const budgetFinanceTotal = sumVals(budgetFinanceItems);
  const expenseTotal = operatingTotal; // backwards compat

  return (
    <div className="flex flex-col gap-16">
      {/* ── Top toolbar: download + expand/collapse all ── */}
      <div className="flex justify-end items-center gap-8" style={{ marginTop: -4, flexWrap: "wrap" }}>
        <DownloadMenu filename="input_data" getData={() => {
          // Flat export: sub-units first, then each assumption row with
          // its year-wise values. Each row carries its section in the
          // "Section" column so users can re-pivot in Excel.
          const _val = (key, yr) => {
            const a = assumptions.find((x) => x.key === key && (yr == null ? x.year == null : x.year === yr));
            if (!a) return "";
            const v = parseFloat(a.value);
            if (isNaN(v)) return "";
            // Percent-style keys → show as %; rate-of-interest → %; rest → 2-decimal.
            if (/(%|Growth|Rate|ratio)/i.test(key)) return (v * 100).toFixed(2) + "%";
            return v.toFixed(2);
          };
          const yearCols = years.map((y) => ({ key: `y${y}`, label: `FY ${y}` }));
          const rows = [];
          // Sub-units
          sub_units.forEach((u) => rows.push({
            section: "Sub-units",
            particulars: u.name,
            status: u.status || "",
            keys: u.keys || 0,
            ...Object.fromEntries(years.map((y) => [`y${y}`, ""])),
            _formula_text: "",
          }));
          // Assumption keys grouped by section
          default_keys.forEach((k) => {
            const sec = k.section || "assumptions";
            const formula = k.formula?.formula_expression || "";
            const row = {
              section: sec.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()),
              particulars: k.key,
              status: "",
              keys: "",
              _formula_text: formula,
            };
            if (k.year_wise) {
              years.forEach((y) => { row[`y${y}`] = _val(k.key, parseInt(y, 10)); });
            } else {
              const v = _val(k.key, null);
              years.forEach((y) => { row[`y${y}`] = v; });
            }
            rows.push(row);
          });
          return {
            title: "Input Data",
            columns: [
              { key: "section", label: "Section" },
              { key: "particulars", label: "Particulars" },
              { key: "status", label: "Date Available / Status" },
              { key: "keys", label: "Keys" },
              ...yearCols,
            ],
            rows,
          };
        }} />
        <button className="btn btn-ghost btn-sm" onClick={expandAll}>Expand all</button>
        <button className="btn btn-ghost btn-sm" onClick={collapseAll}>Collapse all</button>
      </div>

      {/* ── Status / Sub-units ── */}
      <CollapsibleCard
        sectionKey="status"
        collapsed={collapsed.status}
        onToggle={() => toggleCollapsed("status")}
        title="Status"
        subtitle="Sub-units making up this property — keys, operational days and contribution to current FY."
        actions={canEdit && !collapsed.status ? (
          <button className="btn btn-primary" onClick={() => setShowAddSub(true)}><PlusIcon /> Add Sub-unit</button>
        ) : null}
      >
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>Sub-project</th>
                <th title="Operational means available for all months; otherwise enter the month it goes live, e.g. Mar'26">Date Available</th>
                <th style={{ textAlign: "right" }}>Keys</th>
                <th style={{ textAlign: "right" }}>Number of Days</th>
                <th style={{ textAlign: "right" }} title="Computed: keys × number of days / total keys">No. of Days</th>
                {canEdit && <th style={{ width: 80 }} />}
              </tr>
            </thead>
            <tbody>
              {sub_units.length === 0 && (
                <tr><td colSpan={canEdit ? 6 : 5} className="text-muted" style={{ textAlign: "center", padding: 20 }}>
                  No sub-units yet. Click "Add Sub-unit" to add one.
                </td></tr>
              )}
              {sub_units.map((u) => {
                const isEditing = editingSub?.id === u.id;
                const eff = effectiveDays(u);
                return (
                  <tr key={u.id}>
                    <td className="fw-500">
                      {isEditing
                        ? <input className="input input-sm" style={{ textAlign: "left", width: "100%" }} value={editingSub.name}
                            onChange={(e) => setEditingSub({ ...editingSub, name: e.target.value })} />
                        : u.name}
                    </td>
                    <td>
                      {isEditing ? (() => {
                        const isOperational = editingSub.status === "Operational";
                        const monthValue = isOperational ? "" : labelToMonthInput(editingSub.status);
                        return (
                          <div className="flex items-center gap-6" style={{ flexWrap: "wrap" }}>
                            <div className="tabs" style={{ padding: 2 }}>
                              <button type="button"
                                className={`tab ${isOperational ? "active" : ""}`}
                                style={{ padding: "4px 10px", fontSize: 11.5 }}
                                onClick={() => setEditingSub({ ...editingSub, status: "Operational" })}>
                                Operational
                              </button>
                              <button type="button"
                                className={`tab ${!isOperational ? "active" : ""}`}
                                style={{ padding: "4px 10px", fontSize: 11.5 }}
                                onClick={() => setEditingSub({ ...editingSub, status: editingSub.status && !isOperational ? editingSub.status : "" })}>
                                Date
                              </button>
                            </div>
                            {!isOperational && (
                              <input className="input input-sm" type="month" style={{ width: 145, textAlign: "left" }}
                                value={monthValue}
                                onChange={(e) => setEditingSub({ ...editingSub, status: monthInputToLabel(e.target.value) })} />
                            )}
                          </div>
                        );
                      })() : (u.status === "Operational"
                          ? <span className="badge badge-ongoing">Operational</span>
                          : <span className="badge badge-required">{u.status}</span>)}
                    </td>
                    <td style={{ textAlign: "right" }} className="mono">
                      {isEditing
                        ? <input className="input input-sm" type="number" value={editingSub.keys}
                            onChange={(e) => setEditingSub({ ...editingSub, keys: e.target.value })} />
                        : u.keys}
                    </td>
                    <td style={{ textAlign: "right" }} className="mono">
                      {isEditing
                        ? <input className="input input-sm" type="number" value={editingSub.operational_days}
                            onChange={(e) => setEditingSub({ ...editingSub, operational_days: e.target.value })} />
                        : u.operational_days}
                    </td>
                    <td style={{ textAlign: "right" }} className="mono text-muted" title="Computed: keys × number of days / total keys">
                      {eff || ""}
                    </td>
                    {canEdit && (
                      <td>
                        {isEditing ? (
                          <div className="cell-edit-actions">
                            <button className="cell-save" onClick={handleSaveSubEdit}><CheckIcon /></button>
                            <button className="cell-cancel" onClick={() => setEditingSub(null)}><XIcon /></button>
                          </div>
                        ) : (
                          <div className="flex gap-4 justify-end">
                            <button className="btn-icon" title="Edit" onClick={() => setEditingSub({ ...u })}><EditIcon /></button>
                            <button className="btn-icon" title="Delete" onClick={() => handleDeleteSub(u.id, u.name)}><TrashIcon /></button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {sub_units.length > 0 && (
                <tr style={{ background: "rgba(124,143,245,0.08)", fontWeight: 600 }}>
                  <td>Total Keys / Weighted Avg</td>
                  <td />
                  <td className="mono text-accent" style={{ textAlign: "right" }}>{totalKeys}</td>
                  <td />
                  <td className="mono text-accent" style={{ textAlign: "right" }}>
                    {Math.round(weightedAvgDays * 100) / 100}
                  </td>
                  {canEdit && <td />}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CollapsibleCard>

      {/* ── Rate of Interest (global assumptions) ── */}
      {(keysInSection("rate_of_interest").length > 0 || isAdmin) && (
        <CollapsibleCard
          sectionKey="interest"
          collapsed={collapsed.interest}
          onToggle={() => toggleCollapsed("interest")}
          title="Rate of Interest"
          subtitle="Applied to all years. Stored as decimal (e.g. 8.5 → 0.085).">
          {isAdmin && (
            <div className="flex justify-end mb-12">
              <button className="btn btn-ghost btn-sm"
                onClick={() => openAddKeyModal("rate_of_interest")}>
                <PlusIcon /> Add Input Variable
              </button>
            </div>
          )}
          <div className="grid-2 gap-16" style={{ maxWidth: 600 }}>
            {keysInSection("rate_of_interest").map((kd) => {
              const k = kd.key;
              const unit = UNIT_HINT[k]?.actual || "% per annum";
              const sym = cellSymbol(k, null) || "%";
              const hasFormula = !!kd.formula;
              return (
                <div key={k} className="form-group" style={{ margin: 0 }}>
                  <label className="form-label flex items-center gap-6" style={{ flexWrap: "wrap" }}>
                    <span>{k}</span>
                    <span className="text-xxs text-dim" style={{ fontWeight: 400 }}>({unit})</span>
                    {isAdmin && (
                      <span className="flex items-center gap-8" style={{ marginLeft: "auto" }}>
                        <button type="button" className="btn-icon"
                          title={hasFormula ? `Formula: ${kd.formula.formula_expression}` : "Add formula"}
                          onClick={() => setFormulaModal({ keyName: k, formula: kd.formula })}
                          style={{ padding: 2, color: hasFormula ? "var(--accent)" : "var(--text-muted)", opacity: hasFormula ? 1 : 0.5, fontStyle: "italic", fontWeight: 600 }}>
                          ƒ
                        </button>
                        <button type="button" className="btn-icon" title="Rename / edit"
                          onClick={() => openEditKeyModal(kd)} style={{ padding: 2 }}>
                          <EditIcon />
                        </button>
                        <button type="button" className="btn-icon" title="Delete"
                          onClick={() => handleDeleteKey(kd)} style={{ padding: 2 }}>
                          <XIcon />
                        </button>
                      </span>
                    )}
                  </label>
                  <div style={{ position: "relative", maxWidth: 220 }}>
                    <input className="input mono" type="number" step="0.01"
                      disabled={!canEdit}
                      value={fmtCell(k, getValue(k, null))}
                      onChange={(e) => setDraftValue(k, null, parseCell(k, e.target.value))}
                      style={{ paddingRight: 30 }} />
                    <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: 12 }}>{sym}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleCard>
      )}

      {/* Budget Summary moved to Input → Budget Summary tab. */}

      {/* ── Income & Expense Breakdown (FY actual) ── */}
      <CollapsibleCard
        sectionKey="breakdown"
        collapsed={collapsed.breakdown}
        onToggle={() => toggleCollapsed("breakdown")}
        title="Income & Expense Breakdown"
        subtitle="FY actual breakup. The totals feed Operating Expenses and Corporate Expenses Actual values automatically. Values in ₹.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 18 }}>
          {[
            { id: "other_income",       title: "Other Income",       color: "var(--green-light)", items: incomeItems,    total: incomeTotal,    showCorporateFlag: false, subtotalRow: null },
            { id: "operating_expense",  title: "Operating Expenses", color: "var(--red-light)",   items: operatingItems, total: operatingTotalAll, showCorporateFlag: true,
              subtotalRow: corporateTotal > 0 ? { label: "Of which Corporate", value: corporateTotal } : null },
          ].map((sec) => (
            <div key={sec.id} style={{ background: "rgba(255,255,255,0.025)", borderRadius: 12, padding: 18, border: "1px solid var(--border)" }}>
              <h4 className="fw-600 mb-12" style={{ fontSize: 14.5, color: sec.color }}>{sec.title}</h4>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th style={{ textAlign: "right" }}>Value (₹)</th>
                      {sec.showCorporateFlag && <th style={{ textAlign: "center", width: 80 }} title="Tag this row as Corporate Expense">Corp.</th>}
                      {canEdit && <th style={{ width: 70 }} />}
                    </tr>
                  </thead>
                  <tbody>
                    {sec.items.length === 0 && (
                      <tr><td colSpan={(canEdit ? 1 : 0) + (sec.showCorporateFlag ? 3 : 2)} className="text-muted text-xs" style={{ textAlign: "center", padding: 14 }}>
                        No items yet.
                      </td></tr>
                    )}
                    {sec.items.map((it) => {
                      const isEditing = editingLineItem?.id === it.id;
                      const itIsCorp = isCorporateItem(it);
                      return (
                        <React.Fragment key={it.id}>
                          <tr style={itIsCorp ? { background: "rgba(251,191,36,0.06)" } : undefined}>
                            <td>
                              {isEditing
                                ? <input className="input input-sm" style={{ textAlign: "left", width: "100%" }}
                                    value={editingLineItem.name}
                                    onChange={(e) => setEditingLineItem({ ...editingLineItem, name: e.target.value })} />
                                : <span className="text-sm">{it.name}</span>}
                            </td>
                            <td style={{ textAlign: "right" }} className="mono">
                              {isEditing
                                ? <input className="input input-sm mono" type="number"
                                    value={editingLineItem.value}
                                    onChange={(e) => setEditingLineItem({ ...editingLineItem, value: e.target.value })} />
                                : fmtMoney(it.value)}
                            </td>
                            {sec.showCorporateFlag && (
                              <td style={{ textAlign: "center" }}>
                                <input type="checkbox"
                                  disabled={!canEdit}
                                  checked={itIsCorp}
                                  title="Tag as Corporate Expense — counted in Corporate Expenses Variable"
                                  onChange={async (e) => {
                                    try {
                                      // Move legacy "corporate_expense" rows to operating_expense + flag.
                                      await api.updateProjectLineItem(projectId, it.id, {
                                        category: "operating_expense",
                                        is_corporate: e.target.checked,
                                      });
                                      load();
                                      notifyChange();
                                    } catch (err) { toast(err.message, "error"); }
                                  }}
                                  style={{ accentColor: "var(--orange-light)" }} />
                              </td>
                            )}
                            {canEdit && (
                              <td>
                                {isEditing ? (
                                  <div className="cell-edit-actions">
                                    <button className="cell-save" onClick={handleSaveLineItemEdit}><CheckIcon /></button>
                                    <button className="cell-cancel" onClick={() => setEditingLineItem(null)}><XIcon /></button>
                                  </div>
                                ) : (
                                  <div className="flex gap-4 justify-end">
                                    <button className="btn-icon" title="Edit / move category"
                                      onClick={() => setEditingLineItem({ id: it.id, name: it.name, value: it.value, category: it.category })}>
                                      <EditIcon />
                                    </button>
                                    <button className="btn-icon" title="Remove"
                                      onClick={() => handleDeleteLineItem(it.id)}><TrashIcon /></button>
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                          {isEditing && (
                            <tr>
                              <td colSpan={(canEdit ? 1 : 0) + (sec.showCorporateFlag ? 3 : 2)} style={{ background: "rgba(124,143,245,0.04)" }}>
                                <div className="flex items-center gap-8" style={{ flexWrap: "wrap" }}>
                                  <span className="text-xxs text-muted">Move to:</span>
                                  <select className="input select" style={{ width: "auto", padding: "4px 28px 4px 10px", fontSize: 12 }}
                                    value={editingLineItem.category || it.category}
                                    onChange={(e) => setEditingLineItem({ ...editingLineItem, category: e.target.value })}>
                                    {LINE_ITEM_CATEGORIES.map((c) => (
                                      <option key={c.id} value={c.id}>{c.label}</option>
                                    ))}
                                  </select>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {sec.items.length > 0 && (
                      <>
                        {sec.subtotalRow && (
                          <tr style={{ background: "rgba(251,191,36,0.10)", fontWeight: 500 }}>
                            <td className="text-xs" style={{ paddingLeft: 24 }}>{sec.subtotalRow.label}</td>
                            <td className="mono" style={{ textAlign: "right", color: "var(--orange-light)" }}>{fmtMoney(sec.subtotalRow.value)}</td>
                            {sec.showCorporateFlag && <td />}
                            {canEdit && <td />}
                          </tr>
                        )}
                        <tr style={{ background: "rgba(124,143,245,0.10)", fontWeight: 600 }}>
                          <td>Total</td>
                          <td className="mono text-accent" style={{ textAlign: "right" }}>{fmtMoney(sec.total)}</td>
                          {sec.showCorporateFlag && <td />}
                          {canEdit && <td />}
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {canEdit && (
                <div className="flex gap-6 mt-12" style={{ flexWrap: "wrap" }}>
                  <input className="input input-sm" placeholder="Particular (e.g. Sale of services)"
                    style={{ flex: "1 1 180px", textAlign: "left" }}
                    value={newLineItem.category === sec.id ? newLineItem.name : ""}
                    onChange={(e) => setNewLineItem({
                      category: sec.id, name: e.target.value,
                      value: newLineItem.category === sec.id ? newLineItem.value : 0,
                      is_corporate: newLineItem.category === sec.id ? !!newLineItem.is_corporate : false,
                    })} />
                  <input className="input input-sm mono" type="number" placeholder="Value (₹)" style={{ width: 130 }}
                    value={newLineItem.category === sec.id ? newLineItem.value : ""}
                    onChange={(e) => setNewLineItem({
                      category: sec.id,
                      name: newLineItem.category === sec.id ? newLineItem.name : "",
                      value: e.target.value,
                      is_corporate: newLineItem.category === sec.id ? !!newLineItem.is_corporate : false,
                    })} />
                  {sec.showCorporateFlag && (
                    <label className="flex items-center gap-6 text-xxs" style={{ cursor: "pointer", color: "var(--orange-light)" }}>
                      <input type="checkbox"
                        checked={newLineItem.category === sec.id && !!newLineItem.is_corporate}
                        onChange={(e) => setNewLineItem({
                          category: sec.id,
                          name: newLineItem.category === sec.id ? newLineItem.name : "",
                          value: newLineItem.category === sec.id ? newLineItem.value : 0,
                          is_corporate: e.target.checked,
                        })}
                        style={{ accentColor: "var(--orange-light)" }} />
                      Corp.
                    </label>
                  )}
                  <button className="btn btn-ghost btn-sm"
                    disabled={newLineItem.category !== sec.id || !newLineItem.name.trim()}
                    onClick={() => handleAddLineItem(sec.id)}>
                    <PlusIcon /> Add
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </CollapsibleCard>

      {/* ── Other Inputs (Operating + Corporate Expenses, ₹ Cr per year) ── */}
      <CollapsibleCard
        sectionKey="otherInputs"
        collapsed={collapsed.otherInputs}
        onToggle={() => toggleCollapsed("otherInputs")}
        title="Other Inputs"
        subtitle="Year-wise Operating and Corporate Expenses (₹ Cr). Actual values auto-populate from the Income & Expense Breakdown section above; Budgeted and Projected are entered manually.">
        {isAdmin && (
          <div className="flex justify-end mb-12">
            <button className="btn btn-ghost btn-sm" onClick={() => openAddKeyModal("other_inputs")}>
              <PlusIcon /> Add Input Variable
            </button>
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ minWidth: 220 }}>Item</th>
                {years.map((y) => {
                  const kind = yearKind(y);
                  const isAB = kind === "actual" || kind === "budgeted";
                  const isHist = kind === "historical";
                  const bg = isHist ? "rgba(148,163,184,0.10)"
                    : isAB ? "rgba(96,165,250,0.18)"
                    : "rgba(124,143,245,0.10)";
                  const border = isHist ? "2px dashed rgba(148,163,184,0.35)"
                    : isAB ? "3px solid rgba(96,165,250,0.55)"
                    : "2px solid rgba(124,143,245,0.30)";
                  const labelColor = isHist ? "var(--text-dim)"
                    : isAB ? "var(--blue)"
                    : "var(--accent)";
                  return (
                    <th key={y} style={{
                      textAlign: "right", background: bg, borderTop: border,
                    }}>
                      <div className="text-xxs" style={{ color: labelColor, fontWeight: 700, letterSpacing: 0.6 }}>
                        {kind === "actual" ? "Actual" : kind === "budgeted" ? "Budgeted"
                          : kind === "historical" ? "Historical" : "Projected"}
                      </div>
                      FY {y}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {keysInSection("other_inputs")
                .filter((k) => k.year_wise)
                .map((k) => ({ key: k.key, auto: OTHER_INPUT_AUTO_MAP[k.key] || null }))
                .map(({ key: k, auto }) => {
                const autoSum = auto === "operating" ? operatingTotal
                              : auto === "corporate" ? corporateTotal
                              : 0;
                const autoActual = autoSum / 1e7;
                return (
                  <tr key={k}>
                    <td className="fw-500">
                      <div className="flex items-center gap-6" style={{ flexWrap: "wrap" }}>
                        <span>{k}</span>
                        {isAdmin && keyMeta[k] && (
                          <span className="flex items-center gap-8" style={{ marginLeft: "auto" }}>
                            <button type="button" className="btn-icon"
                              title={keyMeta[k].formula ? `Formula: ${keyMeta[k].formula.formula_expression}` : "Add formula"}
                              onClick={() => setFormulaModal({ keyName: k, formula: keyMeta[k].formula })}
                              style={{ padding: 2, color: keyMeta[k].formula ? "var(--accent)" : "var(--text-muted)", opacity: keyMeta[k].formula ? 1 : 0.5, fontStyle: "italic", fontWeight: 600 }}>
                              ƒ
                            </button>
                            <button type="button" className="btn-icon" title="Rename / edit"
                              onClick={() => openEditKeyModal(keyMeta[k])} style={{ padding: 2 }}>
                              <EditIcon />
                            </button>
                            <button type="button" className="btn-icon" title="Delete"
                              onClick={() => handleDeleteKey(keyMeta[k])} style={{ padding: 2 }}>
                              <XIcon />
                            </button>
                          </span>
                        )}
                      </div>
                      <div className="text-xxs text-dim mt-4" style={{ fontWeight: 400 }}>
                        {auto
                          ? "Actual auto from breakdown · Budgeted & Projected: ₹ Cr"
                          : "Actual / Budgeted / Projected: ₹ Cr"}
                      </div>
                    </td>
                    {years.map((y, idx) => {
                      const isAB = idx <= 1;
                      const cellBg = isAB ? "rgba(96,165,250,0.10)" : "rgba(124,143,245,0.05)";
                      const leftBorder = idx === 0 ? "3px solid rgba(96,165,250,0.55)" : undefined;
                      // Actual cell — read-only auto from breakdown only when this row has an auto source
                      if (idx === 0 && auto) {
                        return (
                          <td key={y} style={{ textAlign: "right", background: cellBg, borderLeft: leftBorder, position: "relative" }}>
                            <CellSourceBadge kind="auto"
                              title={`Auto-calculated from ${k} breakdown sum ÷ 1 Cr`} />
                            <div style={{ position: "relative", display: "inline-block" }}>
                              <input className="input input-sm mono" type="number" disabled readOnly
                                title={`Auto = sum of ${k} breakdown ÷ 1 Cr (₹ ${autoSum.toLocaleString("en-IN")})`}
                                value={autoActual ? autoActual.toFixed(2) : ""}
                                style={{ paddingRight: 30, opacity: 0.85 }} />
                              <span style={{
                                position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                                color: "var(--text-dim)", fontSize: 11, pointerEvents: "none",
                                fontFamily: "'JetBrains Mono', monospace",
                              }}>Cr</span>
                            </div>
                          </td>
                        );
                      }
                      return (
                        <td key={y} style={{ textAlign: "right", background: cellBg, position: "relative" }}>
                          {canEdit && <CellSourceBadge kind="manual" />}
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <input className="input input-sm mono" type="number" step="any"
                              disabled={!canEdit}
                              placeholder="₹ Cr"
                              value={fmtCell(k, getValue(k, y), idx)}
                              onChange={(e) => setDraftValue(k, y, parseCell(k, e.target.value, idx))}
                              style={{ paddingRight: 30 }} />
                            <span style={{
                              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                              color: "var(--text-dim)", fontSize: 11, pointerEvents: "none",
                              fontFamily: "'JetBrains Mono', monospace",
                            }}>Cr</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CollapsibleCard>

      {/* ── Year-wise assumptions ── */}
      <CollapsibleCard
        sectionKey="assumptions"
        collapsed={collapsed.assumptions}
        onToggle={() => toggleCollapsed("assumptions")}
        title="Assumptions"
        subtitle="Per-year growth rates, ratios, and reference values."
      >
        {isAdmin && (
          <div className="flex justify-end mb-12">
            <button className="btn btn-ghost btn-sm" onClick={() => openAddKeyModal("assumptions")}>
              <PlusIcon /> Add Input Variable
            </button>
          </div>
        )}
        <div className="info-box mb-16" style={{ padding: "10px 14px" }}>
          <div className="flex items-center gap-12 mb-6" style={{ flexWrap: "wrap" }}>
            <span className="flex items-center gap-6 text-xs">
              <span style={{ width: 10, height: 10, borderRadius: 3, background: "rgba(96,165,250,0.30)", border: "1px solid rgba(96,165,250,0.55)", display: "inline-block" }} />
              <strong>Actual / Budgeted</strong> — enter the actual reference value in the unit shown next to each row.
            </span>
            <span className="flex items-center gap-6 text-xs">
              <span style={{ width: 10, height: 10, borderRadius: 3, background: "rgba(124,143,245,0.18)", border: "1px solid rgba(124,143,245,0.35)", display: "inline-block" }} />
              <strong>Projected</strong> — enter growth/ratio in % (e.g. 3 means 3%).
            </span>
          </div>
          <p className="text-xxs text-muted">
            For Occupancy, each Actual / Budgeted cell can switch between <strong>%</strong> and <strong>Rooms Sold</strong> independently.
          </p>
          <p className="text-xxs text-muted mt-6">
            <span className="flex items-center" style={{ display: "inline-flex", gap: 4, marginRight: 14 }}>
              <span style={{ color: "var(--accent)", display: "inline-flex" }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" /></svg>
              </span>
              <strong>Auto</strong> — value is calculated for you.
            </span>
            <span className="flex items-center" style={{ display: "inline-flex", gap: 4 }}>
              <span style={{ color: "var(--orange-light)", display: "inline-flex" }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>
              </span>
              <strong>Manual</strong> — type the value yourself.
            </span>
          </p>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ minWidth: 280 }}>Assumption</th>
                {years.map((y) => {
                  const kind = yearKind(y);
                  const isAB = kind === "actual" || kind === "budgeted";
                  const isHist = kind === "historical";
                  const bg = isHist ? "rgba(148,163,184,0.10)"
                    : isAB ? "rgba(96,165,250,0.18)"
                    : "rgba(124,143,245,0.10)";
                  const border = isHist ? "2px dashed rgba(148,163,184,0.35)"
                    : isAB ? "3px solid rgba(96,165,250,0.55)"
                    : "2px solid rgba(124,143,245,0.30)";
                  const labelColor = isHist ? "var(--text-dim)"
                    : isAB ? "var(--blue)"
                    : "var(--accent)";
                  return (
                    <th key={y} style={{
                      textAlign: "right", background: bg, borderTop: border,
                    }}>
                      <div className="text-xxs" style={{ color: labelColor, fontWeight: 700, letterSpacing: 0.6 }}>
                        {kind === "actual" ? "Actual" : kind === "budgeted" ? "Budgeted"
                          : kind === "historical" ? "Historical" : "Projected"}
                      </div>
                      FY {y}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {keysInSection("assumptions")
                .filter((k) => k.year_wise)
                .map((k) => k.key)
                .map((k) => {
                const unit = UNIT_HINT[k] || { actual: "value", projected: "%" };
                const isOccupancyRow = k === "Occupancy Growth %";
                const isOpExRow = k === "Operating Expenses";
                return (
                <tr key={k}>
                  <td className="fw-500">
                    <div className="flex items-center gap-6" style={{ flexWrap: "wrap" }}>
                      <span>{rowDisplayName(k)}</span>
                      {isAdmin && keyMeta[k] && (
                        <span className="flex items-center gap-8" style={{ marginLeft: "auto" }}>
                          <button type="button" className="btn-icon"
                            title={keyMeta[k].formula ? `Formula: ${keyMeta[k].formula.formula_expression}` : "Add formula"}
                            onClick={() => setFormulaModal({ keyName: k, formula: keyMeta[k].formula })}
                            style={{ padding: 2, color: keyMeta[k].formula ? "var(--accent)" : "var(--text-muted)", opacity: keyMeta[k].formula ? 1 : 0.5, fontStyle: "italic", fontWeight: 600 }}>
                            ƒ
                          </button>
                          <button type="button" className="btn-icon" title="Rename / edit"
                            onClick={() => openEditKeyModal(keyMeta[k])} style={{ padding: 2 }}>
                            <EditIcon />
                          </button>
                          <button type="button" className="btn-icon" title="Delete"
                            onClick={() => handleDeleteKey(keyMeta[k])} style={{ padding: 2 }}>
                            <XIcon />
                          </button>
                        </span>
                      )}
                    </div>
                    <div className="text-xxs text-dim mt-4" style={{ fontWeight: 400, lineHeight: 1.5 }}>
                      <span style={{ color: "var(--blue)" }}>Actual / Budgeted:</span> {unit.actual}
                      <span style={{ color: "var(--text-dim)", margin: "0 6px" }}>·</span>
                      <span style={{ color: "var(--accent)" }}>Projected:</span> {unit.projected}
                      {isOpExRow && <div className="mt-4">Actual = sum of expense breakdown ÷ 1 Cr · auto-populated.</div>}
                    </div>
                  </td>
                  {years.map((y, idx) => {
                    const isActualOrBudget = idx <= 1;
                    const cellBg = isActualOrBudget
                      ? "rgba(96,165,250,0.10)"
                      : "rgba(124,143,245,0.05)";
                    // ── Per-cell mode toggle for Occupancy Growth (Actual / Budgeted only) ──
                    if (isOccupancyRow && isActualOrBudget) {
                      const mode = getOccMode(y);
                      const storeKey = mode === "rooms" ? "Rooms Sold" : "Occupancy Growth %";
                      const sym = mode === "rooms" ? "" : "%";
                      const placeholder = mode === "rooms" ? "rooms / day" : "actual %";
                      return (
                        <td key={y} style={{ textAlign: "right", background: cellBg, borderLeft: idx === 0 ? "3px solid rgba(96,165,250,0.55)" : undefined, position: "relative" }}>
                          {canEdit && <CellSourceBadge kind="manual" />}
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                            <div className="tabs" style={{ padding: 2 }}>
                              <button type="button" className={`tab ${mode === "pct" ? "active" : ""}`}
                                disabled={!canEdit}
                                style={{ padding: "2px 8px", fontSize: 10.5 }}
                                onClick={() => setOccMode(y, "pct")}>%</button>
                              <button type="button" className={`tab ${mode === "rooms" ? "active" : ""}`}
                                disabled={!canEdit}
                                style={{ padding: "2px 8px", fontSize: 10.5 }}
                                onClick={() => setOccMode(y, "rooms")}>Rooms</button>
                            </div>
                            <div style={{ position: "relative", display: "inline-block" }}>
                              <input className="input input-sm mono" type="number" step="any"
                                disabled={!canEdit}
                                placeholder={placeholder}
                                value={fmtCell(storeKey, getValue(storeKey, y), idx)}
                                onChange={(e) => setDraftValue(storeKey, y, parseCell(storeKey, e.target.value, idx))}
                                style={sym ? { paddingRight: 22 } : undefined} />
                              {sym && (
                                <span style={{
                                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                                  color: "var(--text-dim)", fontSize: 11, pointerEvents: "none",
                                  fontFamily: "'JetBrains Mono', monospace",
                                }}>{sym}</span>
                              )}
                            </div>
                          </div>
                        </td>
                      );
                    }
                    // ── Operating Expenses: Actual cell is read-only (auto from breakdown sum) ──
                    if (isOpExRow && idx === 0) {
                      const autoVal = expenseTotal / 1e7;
                      return (
                        <td key={y} style={{ textAlign: "right", background: cellBg, borderLeft: "3px solid rgba(96,165,250,0.55)", position: "relative" }}>
                          <CellSourceBadge kind="auto"
                            title="Auto-calculated from Operating Expenses breakdown sum ÷ 1 Cr" />
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <input className="input input-sm mono" type="number" step="any"
                              disabled
                              title={`Auto = sum of Operating Expenses breakdown (₹ ${expenseTotal.toLocaleString("en-IN")}) ÷ 1 Cr`}
                              value={autoVal ? autoVal.toFixed(2) : ""}
                              readOnly
                              style={{ paddingRight: 30, opacity: 0.85 }} />
                            <span style={{
                              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                              color: "var(--text-dim)", fontSize: 11, pointerEvents: "none",
                              fontFamily: "'JetBrains Mono', monospace",
                            }}>Cr</span>
                          </div>
                        </td>
                      );
                    }
                    const projUnit = cellUnit(k, idx);
                    const placeholder = isActualOrBudget
                      ? "actual value"
                      : (projUnit === "%" ? "enter %"
                          : projUnit ? projUnit : "value");
                    const sym = cellSymbol(k, idx);
                    // Rows with a saved per-key formula (project.formulas) compute
                    // projected (and sometimes actual) values automatically.
                    // Mark such cells with the auto badge unless they're an
                    // Actual/Budgeted manual-entry input.
                    const hasFormula = !!(keyMeta[k] && keyMeta[k].formula);
                    const cellKind = hasFormula && !isActualOrBudget ? "auto" : (canEdit ? "manual" : null);
                    return (
                      <td key={y} style={{ textAlign: "right", background: cellBg, borderLeft: idx === 0 ? "3px solid rgba(96,165,250,0.55)" : undefined, position: "relative" }}>
                        {cellKind && <CellSourceBadge kind={cellKind}
                          title={cellKind === "auto"
                            ? `Auto-calculated by formula: ${keyMeta[k].formula.formula_expression}`
                            : undefined} />}
                        <div style={{ position: "relative", display: "inline-block" }}>
                          <input className="input input-sm mono" type="number" step="any"
                            disabled={!canEdit}
                            placeholder={placeholder}
                            title={isActualOrBudget
                              ? `Enter the actual ${rowDisplayName(k).replace(/ Growth$/, "").replace(/ %.*$/, "")} value (${unit.actual}) for FY ${y}`
                              : `Enter ${rowDisplayName(k).replace(/ %.*$/, "")} ${projUnit === "%" ? "growth/ratio in %" : "value (" + projUnit + ")"} for FY ${y}`}
                            value={fmtCell(k, getValue(k, y), idx)}
                            onChange={(e) => setDraftValue(k, y, parseCell(k, e.target.value, idx))}
                            style={sym ? { paddingRight: sym.length > 1 ? 30 : 22 } : undefined} />
                          {sym && (
                            <span style={{
                              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                              color: "var(--text-dim)", fontSize: 11, pointerEvents: "none",
                              fontFamily: "'JetBrains Mono', monospace",
                            }}>{sym}</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CollapsibleCard>

      {/* ── Tab-wide save bar ── */}
      {canEdit && dirty && (
        <div style={{
          position: "sticky", bottom: 12, zIndex: 20,
          marginTop: 4, padding: "12px 18px",
          background: "var(--bg-elev-1)",
          border: "1px solid var(--border-strong)",
          borderRadius: 12,
          boxShadow: "var(--shadow-lg)",
        }}>
          <div className="flex justify-between items-center gap-12" style={{ flexWrap: "wrap" }}>
            <span className="text-sm">
              <strong className="text-accent">{Object.keys(draft).length}</strong> unsaved change{Object.keys(draft).length === 1 ? "" : "s"} across Room Inventory, Rate of Interest and Assumptions.
            </span>
            <div className="flex gap-10">
              <button className="btn btn-ghost" disabled={saving} onClick={() => setDraft({})}>Discard</button>
              <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Sub-unit Modal ── */}
      {showAddSub && (
        <Modal title="Add Sub-unit" onClose={() => setShowAddSub(false)}>
          <div className="form-group">
            <label className="form-label">Sub-project</label>
            <input className="input" autoFocus value={newSub.name}
              onChange={(e) => setNewSub({ ...newSub, name: e.target.value })}
              placeholder="e.g. Existing Chia, Chia Extension, Banquet" />
          </div>
          <div className="form-group">
            <label className="form-label">Date Available</label>
            {(() => {
              const isOperational = newSub.status === "Operational";
              const monthValue = isOperational ? "" : labelToMonthInput(newSub.status);
              return (
                <div className="flex items-center gap-10" style={{ flexWrap: "wrap" }}>
                  <div className="tabs" style={{ padding: 3 }}>
                    <button type="button"
                      className={`tab ${isOperational ? "active" : ""}`}
                      onClick={() => setNewSub({ ...newSub, status: "Operational" })}>
                      Operational
                    </button>
                    <button type="button"
                      className={`tab ${!isOperational ? "active" : ""}`}
                      onClick={() => setNewSub({ ...newSub, status: newSub.status && !isOperational ? newSub.status : "" })}>
                      Date
                    </button>
                  </div>
                  {!isOperational && (
                    <input className="input" type="month" style={{ maxWidth: 200 }}
                      value={monthValue}
                      onChange={(e) => setNewSub({ ...newSub, status: monthInputToLabel(e.target.value) })} />
                  )}
                </div>
              );
            })()}
            <p className="text-xxs text-dim mt-6">Pick "Operational" if available for all months, or "Date" and select the month it goes live (stored as e.g. Mar'26).</p>
          </div>
          <div className="grid-2 gap-12">
            <div className="form-group">
              <label className="form-label">Keys</label>
              <input className="input" type="number" value={newSub.keys}
                onChange={(e) => setNewSub({ ...newSub, keys: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Number of Days</label>
              <input className="input" type="number" value={newSub.operational_days}
                onChange={(e) => setNewSub({ ...newSub, operational_days: e.target.value })} />
              <p className="text-xxs text-dim mt-4">Days operational once live (typically 365). The "No. of Days" weighted contribution column is computed automatically.</p>
            </div>
          </div>
          <ModalActions onCancel={() => setShowAddSub(false)} onConfirm={handleAddSub}
            confirmLabel="Add" disabled={!newSub.name.trim()} />
        </Modal>
      )}

      {/* ── Add / Edit Input Variable modal (Admin) ── */}
      {editKeyModal && (
        <Modal
          title={editKeyModal.mode === "edit" ? `Edit Input Variable — ${editKeyModal.key}` : "Add Input Variable"}
          width="600px"
          onClose={() => setEditKeyModal(null)}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="input" autoFocus
              value={editKeyModal.key}
              onChange={(e) => setEditKeyModal({ ...editKeyModal, key: e.target.value })}
              placeholder="e.g. Marketing Cost % of Revenue" />
            <p className="text-xxs text-dim mt-4">
              Renames cascade to every project's Input Data row and any formula that references this key by name.
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Section</label>
            <select className="input select" value={editKeyModal.section}
              onChange={(e) => setEditKeyModal({ ...editKeyModal, section: e.target.value })}>
              <option value="rate_of_interest">Rate of Interest (global, single value)</option>
              <option value="other_inputs">Other Inputs (year-wise, ₹ Cr)</option>
              <option value="assumptions">Assumptions (year-wise, % growth/ratio)</option>
            </select>
          </div>
          <div className="form-group">
            <label className="flex items-center gap-10" style={{ cursor: "pointer", padding: "8px 12px",
              borderRadius: 8, border: "1px solid var(--border-input)", background: "rgba(255,255,255,0.03)" }}>
              <input type="checkbox" checked={!!editKeyModal.year_wise}
                onChange={(e) => setEditKeyModal({ ...editKeyModal, year_wise: e.target.checked })}
                style={{ accentColor: "var(--accent)", width: 16, height: 16 }} />
              <span className="text-sm">Year-wise</span>
              <span className="text-xxs text-dim" style={{ marginLeft: "auto" }}>
                Stores one value per FY column. Uncheck for a single global value (typical for Rate of Interest).
              </span>
            </label>
          </div>
          <div className="form-group">
            <label className="form-label">Default Value</label>
            <input className="input mono" type="number" step="any"
              value={editKeyModal.default_value}
              onChange={(e) => setEditKeyModal({ ...editKeyModal, default_value: e.target.value })} />
            <p className="text-xxs text-dim mt-4">
              Used to seed Projected years on every project (Actual + Budgeted start at 0). For year-wise rows only.
            </p>
          </div>
          <ModalActions
            onCancel={() => setEditKeyModal(null)}
            onConfirm={handleSaveKeyModal}
            confirmLabel={editKeyModal.mode === "edit" ? "Save Changes" : "Add Input Variable"}
            disabled={!editKeyModal.key.trim()} />
        </Modal>
      )}

      {/* ── Formula modal for an Input Variable (Admin) ── */}
      {formulaModal && (
        <FormulaModal
          title={`Formula — ${formulaModal.keyName}`}
          initialFormula={formulaModal.formula?.formula_expression || ""}
          variableNames={formulaRefNames}
          selfName={formulaModal.keyName}
          onCancel={() => setFormulaModal(null)}
          onSave={handleSaveFormula} />
      )}
    </div>
  );
}


const fmt = (n, dec = 2) => (n == null || isNaN(n) ? "—" : Number(n).toFixed(dec));

/* ─── Icons ─── */
const ChevronDown = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/* ─── Formula Evaluator ─── */
// Placeholders available in any variable's projection formula:
//   ACTUAL / BUDGETED — same-variable Actual & Budgeted values
//   YEAR_INDEX        — projection index (1, 2, 3, ...)
//   GROWTH_RATE       — (BUD-ACT)/ACT for self-growth
//   PREV              — previous year's value
//   YEAR              — fiscal-year-end (e.g. 2025, 2028)
//   DAYS_IN_FY        — 366 in leap-year FYs (FY28 etc.), else 365
// `{Variable Name}` is substituted with that variable's value for the same
// year (sourced from `varLookup`). If the inside of the braces is actually
// one of the known placeholders, the braces are simply stripped so users
// who type `{DAYS_IN_FY}` get the same result as `DAYS_IN_FY`.
const KNOWN_PLACEHOLDERS = new Set([
  "ACTUAL", "BUDGETED", "YEAR_INDEX", "GROWTH_RATE", "PREV", "YEAR", "DAYS_IN_FY",
  "IS_ACTUAL", "IS_BUDGETED", "IS_PROJECTED",
]);
function evaluateFormula(formulaExpr, actual, budgeted, yearIndex, prev, varLookup, year, selfName) {
  if (!formulaExpr) return budgeted || 0;
  const GROWTH_RATE = actual !== 0 ? (budgeted - actual) / actual : 0;
  const yr = parseInt(year);
  const isLeap = !isNaN(yr) && ((yr % 4 === 0 && yr % 100 !== 0) || yr % 400 === 0);
  const DAYS_IN_FY = isLeap ? 366 : 365;
  // Year-position booleans for mixed actual/projected logic.
  // yearIndex: 0 = Actual, 1 = Budgeted, 2+ = Projected.
  const IS_ACTUAL = yearIndex === 0;
  const IS_BUDGETED = yearIndex === 1;
  const IS_PROJECTED = yearIndex >= 2;
  try {
    let expr = formulaExpr;
    expr = expr.replace(/\{([^}]+)\}/g, (_, name) => {
      const trimmed = name.trim();
      if (KNOWN_PLACEHOLDERS.has(trimmed)) return trimmed;
      // {Var Name@PREV} → previous-year value of another variable.
      const prevMatch = trimmed.match(/^(.+?)\s*@PREV$/i);
      const refName = prevMatch ? prevMatch[1].trim() : trimmed;
      // Self-reference handling. {Self@PREV} resolves to the running `prev`
      // (previous-year value of THIS variable — same as the bare PREV
      // placeholder). Same-year {Self} is circular — return 0.
      if (selfName && refName === selfName) {
        return prevMatch ? String(prev || 0) : "0";
      }
      const targetYear = prevMatch && year != null ? parseInt(year) - 1 : year;
      if (varLookup && targetYear != null) {
        const yvals = varLookup[refName] || {};
        const v = yvals[String(targetYear)];
        return v == null ? "0" : String(v);
      }
      return "0";
    });
    // eslint-disable-next-line no-new-func
    const fn = new Function(
      "ACTUAL", "BUDGETED", "YEAR_INDEX", "GROWTH_RATE", "PREV", "YEAR", "DAYS_IN_FY",
      "IS_ACTUAL", "IS_BUDGETED", "IS_PROJECTED", "IF",
      "return " + expr
    );
    // IF(cond, a, b) — spreadsheet-style conditional. Equivalent to `cond ? a : b`,
    // exposed as a function so the same syntax works in both frontend (JS) and
    // backend (Python) evaluators.
    const IF = (cond, a, b) => (cond ? a : b);
    const result = fn(actual, budgeted, yearIndex, GROWTH_RATE, prev || budgeted, yr || 0, DAYS_IN_FY,
      IS_ACTUAL, IS_BUDGETED, IS_PROJECTED, IF);
    return isFinite(result) ? Math.round(result * 100) / 100 : (budgeted || 0);
  } catch {
    return budgeted || 0;
  }
}

function getDefaultFormula() {
  return "BUDGETED * Math.pow(1 + GROWTH_RATE, YEAR_INDEX)";
}

/* ─── Tooltip ─── */
function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  if (!text) return children;
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <span style={{
          position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
          background: "#1a1f36", border: "1px solid rgba(255,255,255,0.12)", color: "var(--text-secondary)",
          padding: "6px 10px", borderRadius: 8, fontSize: 11, whiteSpace: "nowrap", zIndex: 100,
          maxWidth: 280, textWrap: "wrap", boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          pointerEvents: "none",
        }}>
          {text}
        </span>
      )}
    </span>
  );
}

function fmtTime(ts) {
  if (!ts) return "";
  const d = new Date(ts); const diff = (new Date() - d) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/* ─── Project History Tab ─── */
// Note: PreviousActualYearsPanel was removed. Historical year columns are
// now shown directly in the variable tables (toggled via "Show Historical").

function ProjectHistoryTab({ projectId, onLoaded }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ users: [], entity_types: [] });
  const [filters, setFilters] = useState({
    user_id: "", variable_name: "", entity_type: "",
    since: "", until: "", limit: 200,
  });

  useEffect(() => {
    api.getProjectHistoryFacets(projectId)
      .then((f) => setFacets(f || { users: [], entity_types: [] }))
      .catch(() => {});
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    api.getProjectHistory(projectId, filters)
      .then((h) => {
        setHistory(h);
        if (typeof onLoaded === "function") onLoaded(h);
      })
      .catch(() => { setHistory([]); if (typeof onLoaded === "function") onLoaded([]); })
      .finally(() => setLoading(false));
  }, [projectId, filters, onLoaded]);

  const setF = (key, value) => setFilters((p) => ({ ...p, [key]: value }));
  const resetFilters = () => setFilters({
    user_id: "", variable_name: "", entity_type: "",
    since: "", until: "", limit: 200,
  });
  const hasFilter = !!(filters.user_id || filters.variable_name
    || filters.entity_type || filters.since || filters.until);

  const grouped = {};
  history.forEach((h) => {
    const d = h.changed_at ? new Date(h.changed_at).toLocaleDateString() : "Unknown";
    if (!grouped[d]) grouped[d] = [];
    grouped[d].push(h);
  });

  return (
    <div>
      <div className="card mb-16" style={{ padding: 14 }}>
        <div className="flex justify-between items-center mb-8" style={{ flexWrap: "wrap", gap: 8 }}>
          <span className="text-xs fw-600" style={{ textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-secondary)" }}>
            Filter
          </span>
          {hasFilter && (
            <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Reset filters</button>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <div>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>User</label>
            <select className="input select" value={filters.user_id}
              onChange={(e) => setF("user_id", e.target.value)}>
              <option value="">— Any user —</option>
              {facets.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>Variable name</label>
            <input className="input" type="text"
              placeholder="e.g. Room Revenue"
              value={filters.variable_name}
              onChange={(e) => setF("variable_name", e.target.value)} />
          </div>
          <div>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>Entity type</label>
            <select className="input select" value={filters.entity_type}
              onChange={(e) => setF("entity_type", e.target.value)}>
              <option value="">— Any —</option>
              {facets.entity_types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>Since</label>
            <input className="input" type="datetime-local"
              value={filters.since}
              onChange={(e) => setF("since", e.target.value)} />
          </div>
          <div>
            <label className="form-label text-xxs" style={{ marginBottom: 4 }}>Until</label>
            <input className="input" type="datetime-local"
              value={filters.until}
              onChange={(e) => setF("until", e.target.value)} />
          </div>
        </div>
      </div>

      {loading && <div className="loading-container"><div className="spinner" /></div>}
      {!loading && history.length === 0 && (
        <p className="text-muted" style={{ padding: 20 }}>
          {hasFilter ? "No changes match the current filters." : "No changes recorded yet."}
        </p>
      )}
      {!loading && history.length > 0 && (
        <p className="text-xxs text-dim mb-12">
          Showing {history.length} change{history.length === 1 ? "" : "s"}{hasFilter ? " (filtered)" : ""}.
        </p>
      )}
      {!loading && Object.entries(grouped).map(([date, entries]) => (
        <div key={date} className="mb-20">
          <p className="text-xxs text-muted fw-600 mb-8" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>{date}</p>
          {entries.map((h) => (
            <div key={h.id} style={{ padding: "12px 16px", marginBottom: 4, borderRadius: 10, background: "rgba(255,255,255,0.02)",
              borderLeft: h.entity_type === "variable_value" ? "3px solid var(--accent)" : "3px solid var(--text-dim)" }}>
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-sm fw-500">{h.changed_by_name || "System"}</span>
                  <span className="text-xs text-muted" style={{ marginLeft: 8 }}>
                    {h.entity_type === "variable_value" && h.variable_name
                      ? `changed ${h.variable_name}${h.year ? ` (FY ${h.year})` : ""}`
                      : `${h.field_name} — ${h.entity_type}`}
                  </span>
                </div>
                <span className="text-xxs text-dim">{h.changed_at ? new Date(h.changed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
              </div>
              {(h.old_value !== null || h.new_value !== null) && (
                <div className="flex gap-8 items-center mt-6">
                  {h.old_value !== null && <span className="mono text-xs" style={{ background: "rgba(245,101,101,0.1)", color: "var(--red-light)", padding: "2px 8px", borderRadius: 4, textDecoration: "line-through" }}>{h.old_value}</span>}
                  <span className="text-xs text-muted">→</span>
                  <span className="mono text-xs" style={{ background: "rgba(72,187,120,0.1)", color: "var(--green-light)", padding: "2px 8px", borderRadius: 4 }}>{h.new_value}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════
   MAIN PROJECT DETAIL
   ══════════════════════════════════════════════════════════════ */
export default function ProjectDetail({ projectId, canEdit, isAdmin = false, onBack, onNameLoaded }) {
  // Variables-tab editing is restricted to Admin (per project rule), and Admin
  // can override even auto-computed cells. This is independent of `canEdit`,
  // which still gates the Assumptions tab + sub-units / line items / etc.
  const canEditVars = isAdmin;
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("assumptions");
  // Snapshot of the latest Change History rows the child has loaded, kept
  // in a ref so the DownloadMenu can pull current data lazily on click.
  const historyRowsRef = useRef([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showAddVar, setShowAddVar] = useState(false);
  const [newVar, setNewVar] = useState({ name: "", category: "Profit & Loss Statement", description: "" });
  // Actual year change is now done from Settings → Project ↔ Company /
  // Vertical Mapping (per-project Actual FY dropdown).
  const [newYear, setNewYear] = useState(2025);
  const [changingVarType, setChangingVarType] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [showFormulas, setShowFormulas] = useState(false);
  const [summaryOnly, setSummaryOnly] = useState(false);
  // Hide historical year columns by default; user toggles via header button.
  const [showHistorical, setShowHistorical] = useState(false);
  // "Add Historical Year" modal state. When non-null, a modal is open with
  // the year about to be added (defaults to actual_year - 1).
  const [addHistYear, setAddHistYear] = useState(null);
  const [savingHistYear, setSavingHistYear] = useState(false);
  // User-defined variable categories (drives tab order in this view).
  const [variableCategories, setVariableCategories] = useState([]);
  const toast = useToast();
  const onNameRef = useRef(onNameLoaded);
  onNameRef.current = onNameLoaded;

  // `silent=true` skips the loading spinner so background refetches after
  // an inline cell edit don't make the page flash.
  const loadProject = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return api.getProject(projectId)
      .then((p) => {
        setProject(p);
        setNewYear(p.actual_year);
        if (onNameRef.current) onNameRef.current(p.name);
      })
      .catch(() => toast("Failed to load project", "error"))
      .finally(() => { if (!silent) setLoading(false); });
  }, [projectId, toast]);

  useEffect(() => { loadProject(); }, [loadProject]);

  // Load the user-managed variable categories once on mount. Used to drive
  // the per-category tab order in this project view.
  useEffect(() => {
    api.getVariableCategories()
      .then((rows) => Array.isArray(rows) ? setVariableCategories(rows) : null)
      .catch(() => {});
  }, []);

  // Derived ordered category list — falls back to the canonical names if
  // the backend hasn't loaded yet.
  const VARIABLE_CATEGORIES = useMemo(() => {
    if (!variableCategories || variableCategories.length === 0) return FALLBACK_CATEGORIES;
    return variableCategories.map((c) => c.name);
  }, [variableCategories]);

  const years = useMemo(() => {
    if (!project) return [];
    const ay = project.actual_year;
    // Backend exposes a `years` array with historical years prepended to
    // the standard Actual/Budgeted/Projected window. Filter historical
    // columns out by default — user expands them via Show Historical.
    const base = Array.isArray(project.years) && project.years.length > 0
      ? project.years.map((y) => parseInt(y, 10)).filter((y) => !isNaN(y))
      : [ay, ay + 1, ay + 2, ay + 3, ay + 4, ay + 5];
    return showHistorical ? base : base.filter((y) => y >= ay);
  }, [project, showHistorical]);

  const yearLabel = (y) => {
    if (!project) return "";
    if (y < project.actual_year) return "Historical";
    if (y === project.actual_year) return "Actual";
    if (y === project.actual_year + 1) return "Budgeted";
    return "Projected";
  };

  const isProjectedYear = (y) => project && y > project.actual_year + 1;
  const isHistoricalYear = (y) => project && y < project.actual_year;
  // Var Type (Constant/Vary) is deprecated — every variable that needs
  // projection logic now lives in the auto-computed pipeline.
  const isConstant = false;

  /* ─── Formula-based auto-calc for projected years ─── */
  const computeProjectedValues = useCallback((variable, newValues) => {
    if (!project) return {};
    const ay = project.actual_year;
    const actual = parseFloat(newValues[ay] ?? variable.values?.[String(ay)] ?? 0);
    const budgeted = parseFloat(newValues[ay + 1] ?? variable.values?.[String(ay + 1)] ?? 0);
    const formulaExpr = project.formulas?.[variable.name]?.formula_expression || getDefaultFormula();
    // Build a year-keyed lookup so the formula can reference {Other Var}.
    // Seed from the server-side formula_lookup (Input Data assumptions +
    // synthesized variables like Total Room Count / ARR / Room Revenue),
    // then overlay the project's stored variable values.
    const varLookup = { ...(project.formula_lookup || {}) };
    (project.variables || []).forEach((v) => {
      if (v.name !== variable.name) varLookup[v.name] = v.values || varLookup[v.name] || {};
    });
    delete varLookup[variable.name];  // don't let formula self-reference
    const projected = {};
    let prev = budgeted;
    for (let i = 2; i <= 5; i++) {
      const yr = ay + i;
      const val = evaluateFormula(formulaExpr, actual, budgeted, i - 1, prev, varLookup, yr, variable.name);
      projected[yr] = val;
      prev = val;
    }
    return projected;
  }, [project]);

  /* ─── Save cell value + auto-calc projected ─── */
  const saveCellEdit = async () => {
    if (!editingCell) return;
    const { varId, year } = editingCell;
    const variable = project.variables.find((v) => v.id === varId);
    if (!variable) return;
    const newVal = parseFloat(editValue) || 0;

    // No-op guard: if the typed value matches what's already displayed
    // (rounded to 4 decimals — same precision the backend stores), skip
    // the API write. Prevents accidental "click cell, press Enter" from
    // persisting computed values as if they were manual overrides.
    const currentRaw = variable.values?.[String(year)];
    const currentRounded = currentRaw == null ? 0 : Math.round(Number(currentRaw) * 10000) / 10000;
    const newRounded = Math.round(newVal * 10000) / 10000;
    if (currentRounded === newRounded) {
      setEditingCell(null);
      return;
    }

    const payload = { [year]: newVal };

    // If editing actual or budgeted → auto-compute projected years
    if (year <= project.actual_year + 1) {
      const tempValues = { ...variable.values, [year]: newVal };
      const projected = computeProjectedValues(variable, tempValues);
      Object.assign(payload, projected);
    }
    if (isConstant && year > project.actual_year + 1) {
      const projected = computeProjectedValues(variable, variable.values);
      Object.assign(payload, projected);
    }

    // Optimistic local update — paint the new value immediately so editing
    // feels Excel-fast. The silent refetch below reconciles with server-
    // computed values (synthesized variables, statement formulas) without
    // flashing the spinner.
    const stringPayload = {};
    Object.entries(payload).forEach(([y, val]) => { stringPayload[String(y)] = val; });
    setProject((p) => p && {
      ...p,
      variables: p.variables.map((vv) =>
        vv.id === varId ? { ...vv, values: { ...(vv.values || {}), ...stringPayload } } : vv
      ),
    });
    setEditingCell(null);

    try {
      await api.updateVariableValues(varId, payload);
      // Silent refetch in the background — picks up cascaded computed
      // values without flashing the loading spinner.
      loadProject(true);
    } catch (e) {
      toast(e.message, "error");
      // Roll back to server truth on failure.
      loadProject(true);
    }
  };

  const handleCellEdit = (variable, year) => {
    if (!canEditVars) return;
    // Synthetic computed rows (no DB id) can't be saved through the values API.
    if (variable.synthetic) {
      toast(`"${variable.name}" is auto-computed and not yet a saved variable. Add it via "Add Variable" first to override.`, "error");
      return;
    }
    // Constant type: non-computed projected years stay formula-driven and read-only.
    // Admin overrides on computed cells ARE allowed (per-cell override persists if non-zero).
    if (!variable.computed && isConstant && isProjectedYear(year)) return;
    setEditingCell({ varId: variable.id, year });
    setEditValue(variable.values?.[String(year)] ?? "");
  };

  const handleAddVariable = async () => {
    if (!newVar.name.trim()) return;
    try {
      await api.addVariable(projectId, { name: newVar.name.trim(), category: newVar.category, description: newVar.description });
      toast(`Variable "${newVar.name}" added`);
      setNewVar({ name: "", category: "Profit & Loss Statement", description: "" });
      setShowAddVar(false);
      loadProject();
    } catch (e) { toast(e.message, "error"); }
  };

  const handleDeleteVariable = async (varId, varName) => {
    try { await api.deleteVariable(varId); toast(`Variable "${varName}" removed`); loadProject(); }
    catch (e) { toast(e.message, "error"); }
  };

  const handleChangeVarType = async (newType) => {
    if (newType === project.projected_var_type) return;
    setChangingVarType(true);
    try { await api.updateProject(projectId, { projected_var_type: newType }); toast(`Variable type changed to ${newType}`); loadProject(); }
    catch (e) { toast(e.message, "error"); }
    setChangingVarType(false);
  };

  // Open the "Add Historical Year" modal. Default = actual_year - 1, but
  // skip past years that already exist as historical columns so the user
  // lands on the next vacant year without an extra click.
  const openAddHistoricalYear = () => {
    if (!project) return;
    const existing = new Set((project.historical_years || []).map((y) => parseInt(y, 10)));
    let candidate = project.actual_year - 1;
    while (existing.has(candidate) && candidate > 1900) candidate -= 1;
    setAddHistYear(candidate);
  };

  const submitAddHistoricalYear = async () => {
    if (!project || addHistYear == null) return;
    const yr = parseInt(addHistYear, 10);
    if (isNaN(yr)) { toast("Enter a valid year", "error"); return; }
    if (yr >= project.actual_year) {
      toast(`Year must be earlier than FY ${project.actual_year}`, "error");
      return;
    }
    setSavingHistYear(true);
    try {
      await api.addProjectHistoricalYear(projectId, yr);
      toast(`Historical year FY ${yr} added`);
      setAddHistYear(null);
      setShowHistorical(true);
      await loadProject(true);
    } catch (e) { toast(e.message, "error"); }
    setSavingHistYear(false);
  };

  const removeHistoricalYear = async (yr) => {
    if (!window.confirm(`Remove historical column FY ${yr}? All values entered for this year will be cleared.`)) return;
    try {
      await api.removeProjectHistoricalYear(projectId, parseInt(yr, 10));
      toast(`Historical year FY ${yr} removed`);
      await loadProject(true);
    } catch (e) { toast(e.message, "error"); }
  };

  // Variable projection formulas are managed in Settings → Variable Templates.
  // The project page only renders their effects (formulaProjections below).

  const toggleCategory = (cat) => setCollapsedCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!project) return <p className="text-muted">Project not found</p>;

  const tabs = [
    { id: "assumptions", label: "Input Data" },
    ...VARIABLE_CATEGORIES.map((c) => ({ id: `cat:${c}`, label: CATEGORY_TAB_LABEL[c] || c })),
    { id: "history", label: "Change History" },
  ];

  /* ─── Sort variables for the single P&L-style table.
        Backend assigns sort_order per the canonical P&L sequence; anything
        without an explicit order falls to the bottom and tie-breaks by name. ─── */
  const orderedVars = (project.variables || [])
    .slice()
    .sort((a, b) => {
      const ao = a.sort_order ?? 9999;
      const bo = b.sort_order ?? 9999;
      if (ao !== bo) return ao - bo;
      return (a.name || "").localeCompare(b.name || "");
    });

  /* ─── Render-time formula evaluation.
        For every non-computed variable that has a saved FormulaConfig, this
        pre-runs the formula across all 6 years and stores a year-indexed map.
        The render loop falls back to these computed values when the stored
        VariableValue is missing/zero.

        Cross-formula refs (e.g. loan schedule: Closing = Opening − Repayment,
        Opening = Closing@PREV) are resolved year-by-year — each year runs
        sub-passes over all formulas until that year's values stabilize, then
        the next year sees finalized prior-year values. ─── */
  const formulaProjections = (() => {
    if (!project) return {};
    const ay = project.actual_year;
    const allVars = project.variables || [];
    const formulaVars = allVars.filter(
      (v) => !v.computed && project.formulas?.[v.name]?.formula_expression
    );
    if (formulaVars.length === 0) return {};

    // Effective stored values per variable (seed + override for cells the
    // user has typed in — non-zero stored values win over formula output).
    const storedByName = {};
    allVars.forEach((v) => { storedByName[v.name] = v.values || {}; });

    const projected = {};
    formulaVars.forEach((v) => { projected[v.name] = {}; });

    // Build a year-aware lookup. Order of precedence (lowest → highest):
    //   1. server-side formula_lookup (Input Data assumptions, synthesized)
    //   2. running formula projections (covers projected years computed so far)
    //   3. non-zero stored values (user-typed Actual/Budgeted overrides)
    const buildLookup = () => {
      const lookup = { ...(project.formula_lookup || {}) };
      allVars.forEach((other) => {
        const stored = storedByName[other.name] || {};
        const proj = projected[other.name] || {};
        const merged = { ...(lookup[other.name] || {}), ...proj };
        Object.entries(stored).forEach(([y, val]) => {
          if (val != null && Number(val) !== 0) merged[y] = Number(val);
        });
        lookup[other.name] = merged;
      });
      return lookup;
    };

    const MAX_SUB_PASSES = 8;
    for (let i = 0; i < 6; i += 1) {
      const yr = ay + i;
      // Sub-passes within this year so same-year cross-formula refs converge.
      // Prior years are already finalized in `projected`, so @PREV refs
      // resolve correctly without further iteration.
      for (let sub = 0; sub < MAX_SUB_PASSES; sub += 1) {
        const lookup = buildLookup();
        let changed = false;
        formulaVars.forEach((v) => {
          const expr = project.formulas[v.name].formula_expression;
          const actual = parseFloat(storedByName[v.name]?.[String(ay)] ?? 0);
          const budgeted = parseFloat(storedByName[v.name]?.[String(ay + 1)] ?? 0);
          const ownLookup = { ...lookup };
          delete ownLookup[v.name];  // can't self-reference inside its own formula
          // Use the prior year's projected value (or budgeted/actual seed for FY0)
          // as the PREV placeholder for "previous year of THIS variable".
          const prev = projected[v.name]?.[yr - 1] ?? (budgeted || actual);
          const val = evaluateFormula(expr, actual, budgeted, i, prev, ownLookup, yr, v.name);
          const old = projected[v.name][yr];
          if (old == null || Math.abs(old - val) > 1e-6) {
            projected[v.name][yr] = val;
            changed = true;
          }
        });
        if (!changed) break;
      }
    }
    return projected;
  })();

  /* ─── Statement renderer ─── */
  const renderStatement = (section) => {
    const data = project.statements?.[section];
    if (!data || Object.keys(data).length === 0) {
      return (
        <div style={{ padding: 30, textAlign: "center" }}>
          <p className="text-muted mb-8">No statement lines configured for this section.</p>
          <p className="text-xs text-dim">Go to Settings → Statement Formulas to define computed lines like:</p>
          <code className="mono text-xs" style={{ display: "block", marginTop: 8, color: "var(--accent)" }}>
            {"{Keys} + {Rooms Under Expansion}"}
          </code>
        </div>
      );
    }

    // Sort entries by sort_order
    const entries = Object.entries(data).sort((a, b) => (a[1].sort_order || 0) - (b[1].sort_order || 0));

    return (
      <div className="table-wrap">
        <table>
          <thead><tr><th style={{ minWidth: 220 }}>Particulars</th>
            {years.map((y) => {
              const lbl = yearLabel(y);
              const tint = lbl === "Historical" ? "var(--text-dim)"
                : lbl === "Actual" || lbl === "Budgeted" ? "var(--blue)"
                : "var(--text-dim)";
              return (
                <th key={y} style={{ textAlign: "right" }}>
                  <div className="text-xxs" style={{ color: tint }}>{lbl}</div>FY {y}
                </th>
              );
            })}
          </tr></thead>
          <tbody>
            {entries.map(([lineItem, info]) => {
              const isBold = info.is_bold || /Total|PAT|EBIDTA|PBT|Net|Closing/i.test(lineItem);
              const hasFormula = !!info.formula;
              return (
                <tr key={lineItem} style={{ borderTop: info.is_separator ? "2px solid rgba(255,255,255,0.08)" : "none" }}>
                  <td style={{ fontWeight: isBold ? 600 : 400 }}>
                    <Tooltip text={hasFormula ? `Formula: ${info.formula}` : null}>
                      <span style={{ borderBottom: hasFormula ? "1px dotted rgba(102,126,234,0.4)" : "none", cursor: hasFormula ? "help" : "default" }}>
                        {lineItem}
                      </span>
                    </Tooltip>
                  </td>
                  {years.map((y) => {
                    const v = info.values?.[String(y)];
                    return (
                      <td key={y} className={`mono text-xs ${v < 0 ? "text-neg" : v > 0 ? "text-pos" : ""}`}
                        style={{ textAlign: "right", fontWeight: isBold ? 600 : 400 }}>
                        {v != null ? fmt(v) : "—"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  /* ─── Variable table row ─── */
  const CR_COMPUTED = new Set([
    "Room Revenue", "F&B Revenue", "OOD Revenue", "Total Revenue",
    "Operating Expenses", "Corporate Expenses",
    "EBIDTA", "PBT", "PAT",
  ]);
  const INTEGER_COMPUTED = new Set([
    "Total Room Count", "Rooms Sold", "No. of Days of Operation",
  ]);
  const renderVarRow = (v) => {
    const isComputed = !!v.computed;
    const isSubtotal = !!v.subtotal;
    const isPctVar = v.name === "Occupancy %" || /(%|rate)/i.test(v.name);
    // Per-variable display formatting for computed rows.
    const formatComputed = (val) => {
      if (val == null) return "—";
      const n = parseFloat(val);
      if (v.name === "Occupancy %") return (n * 100).toFixed(2) + "%";
      if (v.name === "ARR") return "₹ " + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
      if (CR_COMPUTED.has(v.name)) return n.toFixed(2) + " Cr";
      if (INTEGER_COMPUTED.has(v.name)) return Math.round(n).toString();
      return fmt(val);
    };
    const rowStyle = isSubtotal
      ? {
          background: "rgba(124,143,245,0.14)",
          fontWeight: 700,
          borderTop: "2px solid rgba(124,143,245,0.40)",
        }
      : undefined;

    const projectFormula = project.formulas?.[v.name];
    const hasUserFormula = !!projectFormula;
    // A subtotal row with no formula, no system computation, and no stored
    // values acts as a parent / section header — its cells stay empty so
    // the row reads as a label rather than a "0.00 across the board" row.
    const hasAnyStoredValue = !!v.values && Object.values(v.values || {}).some(
      (n) => n != null && Number(n) !== 0
    );
    const hasFormulaProjection = !!formulaProjections?.[v.name]
      && Object.values(formulaProjections[v.name]).some((n) => n != null && Number(n) !== 0);
    const isHeaderOnlyTotal = isSubtotal && !isComputed && !hasUserFormula
      && !hasAnyStoredValue && !hasFormulaProjection;
    return (
      <tr key={v.id} style={rowStyle}>
        <td className={isSubtotal ? "fw-700" : "fw-500"} style={isSubtotal ? { color: "var(--accent)" } : undefined}>
          <div className="flex items-center gap-6" style={{ flexWrap: "wrap" }}>
            <span>{v.name}</span>
            {isSubtotal && !isHeaderOnlyTotal && (
              <span className="text-xxs fw-600" style={{
                padding: "2px 8px", borderRadius: 12,
                background: "rgba(124,143,245,0.20)", color: "var(--accent)",
                border: "1px solid rgba(124,143,245,0.45)",
                letterSpacing: 0.4, textTransform: "uppercase",
              }}>Total</span>
            )}
            {/* Formula indicator (read-only here — edit in Settings → Variable Templates). */}
            {!v.synthetic && hasUserFormula && (
              <span className="btn-icon" title={`Formula: ${projectFormula.formula_expression}\n\nEdit in Settings → Variable Templates.`}
                style={{ padding: 2, color: "var(--accent)", fontStyle: "italic", fontWeight: 600, cursor: "default" }}>
                ƒ
              </span>
            )}
            {/* Derivation indicator for backend-synthesized variables (Total
                Room Count, Occupancy %, EBIDTA, …). Read-only — hover shows
                the auto-derivation rule supplied by build_project_data. */}
            {isComputed && v.description && (
              <span className="btn-icon" title={`Auto-derivation: ${v.description}`}
                style={{ padding: 2, color: "var(--accent)", fontStyle: "italic", fontWeight: 600, cursor: "help" }}>
                ƒ
              </span>
            )}
          </div>
          {/* Always show the derivation rule for computed variables (not
              gated by Show formulas) — surfaces the "where does this number
              come from" question without a hover. User-formula variables
              still gate behind the Show formulas toggle to keep the row
              compact unless requested. */}
          {(isComputed && v.description) && (
            <div className="text-xxs text-dim mt-4" style={{ fontWeight: 400, lineHeight: 1.5, fontStyle: "italic" }}>
              {v.description}
            </div>
          )}
          {showFormulas && !isComputed && hasUserFormula && (
            <div className="text-xxs text-dim mt-4" style={{ fontWeight: 400, lineHeight: 1.5 }}>
              Formula: <code className="mono" style={{ color: "var(--accent)" }}>{projectFormula.formula_expression}</code>
            </div>
          )}
          {showFormulas && !isComputed && !hasUserFormula && v.description && (
            <div className="text-xxs text-dim mt-4" style={{ fontWeight: 400, lineHeight: 1.5 }}>
              {v.description}
            </div>
          )}
        </td>
        <td>
          {isHeaderOnlyTotal
            ? null
            : <span className={v.mandatory ? "badge-required" : "badge-custom"}>{v.mandatory ? "Required" : "Optional"}</span>}
        </td>
        {years.map((y) => {
          const isEditing = editingCell?.varId === v.id && editingCell?.year === y;
          // Stored value first; fall back to render-time formula projection
          // for non-computed variables that have a formula but no stored value
          // (or stored value is zero — typical for unedited new variables).
          const stored = v.values?.[String(y)];
          const formulaVal = formulaProjections?.[v.name]?.[y];
          const val = (stored != null && Number(stored) !== 0)
            ? stored
            : (formulaVal != null ? formulaVal : stored);
          const projected = isProjectedYear(y);
          const historical = isHistoricalYear(y);
          // Cell-source classification. For computed variables, the backend
          // ships a `manual_overrides` list of years where the user typed a
          // non-zero override — the only reliable way to distinguish a user
          // override from a backend-computed value (since both end up in
          // v.values). For non-computed formula variables, a non-zero
          // stored value is treated as a manual override. Variables without
          // any formula/computation stay unmarked.
          const isManualOverride = isComputed
            ? (v.manual_overrides || []).includes(String(y))
            : (stored != null && Number(stored) !== 0);
          const variableHasAutoSource = hasUserFormula || isComputed;
          // Suppress the manual/auto icons for Historical cells — those are
          // snapshots, not formula-driven, so the icon would be misleading.
          const isManualCell = !historical && !v.synthetic && variableHasAutoSource && isManualOverride;
          const isAutoCell = !historical && !v.synthetic && variableHasAutoSource && !isManualOverride
            && (formulaVal != null || isComputed);
          // Pure-manual variables (no formula, no backend computation, no
          // synthetic flag) like Equity Infusion / Tax / Finance Cost.
          // Every editable cell is a manual-entry slot — show the pencil
          // so the user gets the same affordance as formula overrides.
          // Historical cells are still excluded (snapshots).
          const isManualOnlyCell = !historical && !v.synthetic && !isSubtotal
            && !variableHasAutoSource && canEditVars;

          // Header-only subtotal rows: render an empty cell (the row name
          // serves as a section header — no zeros, no dashes).
          if (isHeaderOnlyTotal) {
            return <td key={y} />;
          }

          return (
            <td key={y} style={{ textAlign: "right", position: "relative",
                  background: historical ? "rgba(148,163,184,0.05)"
                    : isAutoCell ? "rgba(124,143,245,0.05)"
                    : (isManualCell || isManualOnlyCell) ? "rgba(245,158,11,0.07)"
                    : undefined }}>
              {isEditing ? (
                <div className="cell-edit-actions">
                  <input className="input input-sm" value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveCellEdit(); if (e.key === "Escape") setEditingCell(null); }}
                    autoFocus />
                  <button className="cell-save" onClick={saveCellEdit}><CheckIcon /></button>
                  <button className="cell-cancel" onClick={() => setEditingCell(null)}><XIcon /></button>
                </div>
              ) : (
                <div className="flex items-center justify-end gap-4">
                  {isAutoCell && (
                    <span title="Auto-generated value. Click to override with a manual entry."
                      style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        color: "var(--accent)",
                      }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
                      </svg>
                    </span>
                  )}
                  {(isManualCell || isManualOnlyCell) && (
                    <span title={isManualCell
                        ? "Manual entry — overrides the formula/computation for this cell. Click to edit."
                        : "Manual entry expected — click to type a value."}
                      style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        color: "var(--orange-light)",
                        opacity: isManualOnlyCell && !isManualCell ? 0.5 : 1,
                      }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                      </svg>
                    </span>
                  )}
                  <span
                    className={`mono text-xs ${canEditVars && !v.synthetic ? "cell-editable" : ""}`}
                    style={{ opacity: isAutoCell ? 0.85 : 1 }}
                    title={
                      v.synthetic
                        ? "Auto-computed (read-only — click 'Add Variable' to override)"
                        : isManualCell
                            ? "Manual entry — overrides the formula/computation for this cell. Click to edit."
                            : isManualOnlyCell
                                ? "Manual entry expected — click to type a value."
                            : isAutoCell
                                ? (hasUserFormula
                                    ? `Auto-generated by formula: ${projectFormula?.formula_expression || ""}\nClick to override.`
                                    : `Auto-computed${
                                        v.name === "Total Room Count" ? " (Source: sub-units)"
                                        : v.name === "Occupancy %" ? (projected
                                            ? " (Source: previous year × growth %)"
                                            : " (Source: Rooms Sold / Total Room Count)")
                                        : ""
                                      }. Click to override.`)
                                : ""
                    }
                    onClick={() => handleCellEdit(v, y)}
                  >
                    {isComputed ? formatComputed(val) : (val != null ? fmt(val) : "—")}
                  </span>
                </div>
              )}
            </td>
          );
        })}
        {canEditVars && (
          <td>{!v.mandatory && !isComputed && <button className="btn-icon" onClick={() => handleDeleteVariable(v.id, v.name)}><TrashIcon /></button>}</td>
        )}
      </tr>
    );
  };

  return (
    <div className="fade-in">
      {/* ── Header ── */}
      <div className="card mb-24">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex gap-16 items-center">
              <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back</button>
              <span className={`badge ${project.status === "Ongoing" ? "badge-ongoing" : "badge-completed"}`}>{project.status === "Ongoing" ? "Under Construction" : project.status === "Completed" ? "Operational" : project.status}</span>
            </div>
            <h2 className="fw-600 mt-12" style={{ fontSize: 22, color: "var(--text-primary)" }}>{project.name}</h2>
          </div>
          {/* Historical-year controls — the default view is Actual + Budgeted
              + 4 Projected. When prior-year actuals are needed, "+ Add
              Historical Year" seeds an editable column (0 values across
              every variable / assumption); the toggle next to it shows or
              hides any existing historical columns. */}
          <div className="flex items-center gap-8" style={{ flexWrap: "wrap" }}>
            {(project.historical_years || []).length > 0 && (
              <button className={`btn btn-sm ${showHistorical ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setShowHistorical((p) => !p)}
                title={showHistorical ? "Hide historical year columns" : "Show historical year columns"}>
                {showHistorical ? "Hide Historical" : `Show Historical (${(project.historical_years || []).length})`}
              </button>
            )}
            {canEdit && (
              <button className="btn btn-ghost btn-sm" onClick={openAddHistoricalYear}
                title={`Add a historical year column (year < FY ${project.actual_year}) for manual data entry.`}>
                <PlusIcon /> Add Historical Year
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-20 mt-16 items-end" style={{ flexWrap: "wrap" }}>
          <div>
            <span className="text-xxs text-muted" style={{ textTransform: "uppercase" }}>Actual Year</span>
            <p className="fw-600 mono text-accent" style={{ fontSize: 14 }}>FY {project.actual_year}</p>
          </div>
          <div><span className="text-xxs text-muted" style={{ textTransform: "uppercase" }}>Company</span><p className="fw-600" style={{ fontSize: 14 }}>{project.company_name || "Unmapped"}</p></div>
          <div><span className="text-xxs text-muted" style={{ textTransform: "uppercase" }}>Variables</span><p className="fw-600" style={{ fontSize: 14 }}>{project.variables?.length || 0}</p></div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="tabs mb-20">
        {tabs.map((t) => (<button key={t.id} className={`tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>))}
      </div>

      {/* ── Per-Category Variable Tabs ── */}
      {activeTab.startsWith("cat:") && (() => {
        const tabCategory = activeTab.slice(4);
        const inCategory = orderedVars.filter((v) => (v.category || "") === tabCategory);
        const filtered = summaryOnly ? inCategory.filter((v) => v.subtotal) : inCategory;
        const subtotalCount = inCategory.filter((v) => v.subtotal).length;

        // ── Build a DownloadMenu payload for this tab. Each variable's
        //    values are formatted the same way they render on screen
        //    (Occupancy → %, ARR → ₹ N, EBIDTA / Total Revenue → "N Cr",
        //    Total Room Count → integer, generic → 2-decimal). The
        //    optional "Formula" column shows the user-defined formula
        //    expression or "Auto" for backend-synthesized rows.
        const formatForExport = (v, val) => {
          if (val == null) return "";
          const n = parseFloat(val);
          if (isNaN(n)) return "";
          if (v.name === "Occupancy %") return (n * 100).toFixed(2) + "%";
          if (v.name === "ARR") return "₹ " + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
          if (CR_COMPUTED.has(v.name)) return n.toFixed(2) + " Cr";
          if (INTEGER_COMPUTED.has(v.name)) return Math.round(n).toString();
          return n.toFixed(2);
        };
        const buildDownload = () => ({
          title: `${project.name || "Project"} — ${tabCategory}`,
          columns: [
            { key: "particulars", label: "Particulars" },
            { key: "type", label: "Type" },
            ...years.map((y) => ({ key: `y${y}`, label: `FY ${y} (${yearLabel(y)})` })),
          ],
          rows: filtered.map((v) => {
            const userFormula = project.formulas?.[v.name]?.formula_expression || "";
            return {
              particulars: v.name,
              type: v.subtotal ? "Subtotal" : v.computed ? "Computed" : (v.mandatory ? "Required" : "Optional"),
              ...Object.fromEntries(years.map((y) => [`y${y}`, formatForExport(v, (v.values || {})[String(y)])])),
              _formula_text: userFormula || (v.computed ? "Auto-computed from inputs" : ""),
            };
          }),
        });
        return (
          <div className="card">
            <div className="flex justify-between items-center mb-16" style={{ flexWrap: "wrap", gap: 12 }}>
              <div>
                <h3 className="heading fw-600" style={{ fontSize: 16 }}>{tabCategory}</h3>
                {!canEditVars && <p className="text-xs text-muted mt-4">Read-only — only Admin users can edit variables on this tab.</p>}
              </div>
              <div className="flex items-center gap-12" style={{ flexWrap: "wrap" }}>
                <div className="tabs" style={{ padding: 3 }}>
                  <button type="button" className={`tab ${!summaryOnly ? "active" : ""}`}
                    onClick={() => setSummaryOnly(false)}>
                    All <span className="text-xxs text-muted" style={{ marginLeft: 6 }}>({inCategory.length})</span>
                  </button>
                  <button type="button" className={`tab ${summaryOnly ? "active" : ""}`}
                    disabled={subtotalCount === 0}
                    onClick={() => setSummaryOnly(true)}>
                    Summary only <span className="text-xxs text-muted" style={{ marginLeft: 6 }}>({subtotalCount})</span>
                  </button>
                </div>
                <label className="flex items-center gap-6" style={{ cursor: "pointer" }}>
                  <input type="checkbox" checked={showFormulas}
                    onChange={(e) => setShowFormulas(e.target.checked)}
                    style={{ accentColor: "var(--accent)" }} />
                  <span className="text-xs text-muted">Show formulas</span>
                </label>
                <DownloadMenu filename={`${project.name || "project"}_${tabCategory}`}
                  getData={buildDownload} />
                {canEditVars && <button className="btn btn-primary" onClick={() => {
                  setNewVar({ name: "", category: tabCategory, description: "" });
                  setShowAddVar(true);
                }}><PlusIcon /> Add Variable</button>}
              </div>
            </div>

            {filtered.length === 0 && (
              <p className="text-muted" style={{ padding: 20 }}>
                {summaryOnly && inCategory.length > 0
                  ? <>No subtotal/summary rows under <strong>{tabCategory}</strong>. Switch to "All" to see every variable.</>
                  : <>No variables under <strong>{tabCategory}</strong> yet. Add one above, or define a template in Settings → Variable Templates.</>}
              </p>
            )}

            {filtered.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th style={{ minWidth: 280, width: "32%" }}>Particulars</th>
                    <th style={{ width: 56, textAlign: "center" }}>Type</th>
                    {years.map((y) => {
                      const lbl = yearLabel(y);
                      const isHist = lbl === "Historical";
                      return (
                        <th key={y} style={{
                          textAlign: "right", width: 96,
                          background: isHist ? "rgba(148,163,184,0.06)" : undefined,
                        }}>
                          <div className="text-xxs" style={{ color: isHist ? "var(--text-dim)" : "var(--text-dim)", fontStyle: isHist ? "italic" : "normal" }}>{lbl}</div>FY {y}
                        </th>
                      );
                    })}
                    {canEditVars && <th style={{ width: 32 }} />}
                  </tr></thead>
                  <tbody>{filtered.map(renderVarRow)}</tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Assumptions Tab ── */}
      {activeTab === "assumptions" && (
        <AssumptionsTab projectId={projectId} canEdit={canEdit} isAdmin={isAdmin}
          showHistorical={showHistorical}
          onChange={() => loadProject(true)} />
      )}

      {activeTab === "history" && (
        <div className="card">
          <div className="flex justify-between items-center mb-16" style={{ flexWrap: "wrap", gap: 8 }}>
            <h3 className="heading fw-600" style={{ fontSize: 16, margin: 0 }}>Change History</h3>
            <DownloadMenu
              filename={`${project.name || "project"}_history`}
              getData={() => ({
                title: `${project.name || "Project"} — Change History`,
                columns: [
                  { key: "when", label: "When" },
                  { key: "user", label: "User" },
                  { key: "entity", label: "Entity" },
                  { key: "field", label: "Field" },
                  { key: "old", label: "Old Value" },
                  { key: "new", label: "New Value" },
                ],
                rows: (historyRowsRef.current || []).map((h) => ({
                  when: h.changed_at ? new Date(h.changed_at).toLocaleString() : "",
                  user: h.changed_by_name || "",
                  entity: `${h.entity_type || ""}${h.variable_name ? ` · ${h.variable_name}` : ""}`,
                  field: h.field_name || "",
                  old: h.old_value == null ? "" : String(h.old_value),
                  new: h.new_value == null ? "" : String(h.new_value),
                  _formula_text: "",
                })),
              })} />
          </div>
          <ProjectHistoryTab projectId={projectId}
            onLoaded={(h) => { historyRowsRef.current = h; }} />
        </div>
      )}

      {/* ── Add Variable Modal ── */}
      {showAddVar && (
        <Modal title="Add Variable" onClose={() => setShowAddVar(false)}>
          <div className="form-group">
            <label className="form-label">Variable Name</label>
            <input className="input" value={newVar.name} onChange={(e) => setNewVar({ ...newVar, name: e.target.value })}
              placeholder="e.g. Rate of Interest" autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Category</label>
            <select className="input select" value={newVar.category}
              onChange={(e) => setNewVar({ ...newVar, category: e.target.value })}>
              {VARIABLE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="text-xxs text-dim mt-4">The category drives which tab this variable shows up in.</p>
          </div>
          <div className="form-group">
            <label className="form-label">Description <span className="text-dim">(optional)</span></label>
            <input className="input" value={newVar.description} onChange={(e) => setNewVar({ ...newVar, description: e.target.value })}
              placeholder="Shown as tooltip on hover" />
          </div>
          <ModalActions onCancel={() => setShowAddVar(false)} onConfirm={handleAddVariable}
            confirmLabel="Add Variable" disabled={!newVar.name.trim()} />
        </Modal>
      )}

      {/* Actual year change moved to Settings → Project ↔ Company / Vertical Mapping. */}

      {/* ── Add Historical Year Modal ──
          Seeds a 0-valued column for every variable + assumption at the
          requested year (must be < Actual FY). Used when the user wants to
          enter prior-year actuals (e.g. FY 2024 when Actual is FY 2025). */}
      {addHistYear != null && (
        <Modal title="Add Historical Year" onClose={() => !savingHistYear && setAddHistYear(null)}>
          <p className="text-xs text-muted mb-12">
            Adds an editable column for the chosen fiscal year. Every variable
            and year-wise assumption is seeded with 0 — type values in directly
            on each statement / Input Data tab.
          </p>
          <div className="form-group">
            <label className="form-label">Fiscal Year</label>
            <input className="input" type="number" autoFocus
              value={addHistYear} min={1900} max={project.actual_year - 1}
              onChange={(e) => setAddHistYear(e.target.value === "" ? "" : parseInt(e.target.value, 10))}
              onKeyDown={(e) => { if (e.key === "Enter" && !savingHistYear) submitAddHistoricalYear(); }} />
            <p className="text-xxs text-dim mt-4">
              Must be earlier than the project's Actual FY ({project.actual_year}).
              {(project.historical_years || []).length > 0 && (
                <> Already added: {(project.historical_years || []).map((y) => `FY ${y}`).join(", ")}.</>
              )}
            </p>
          </div>
          <ModalActions onCancel={() => setAddHistYear(null)}
            onConfirm={submitAddHistoricalYear}
            confirmLabel={savingHistYear ? "Adding…" : "Add Year"}
            disabled={savingHistYear || addHistYear === "" || addHistYear == null
                      || parseInt(addHistYear, 10) >= project.actual_year} />
          {(project.historical_years || []).length > 0 && (
            <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
              <p className="text-xxs text-muted fw-600" style={{ textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
                Existing Historical Columns
              </p>
              <div className="flex" style={{ gap: 6, flexWrap: "wrap" }}>
                {(project.historical_years || []).map((y) => (
                  <span key={y} className="flex items-center"
                    style={{
                      padding: "3px 8px", borderRadius: 6,
                      background: "rgba(148,163,184,0.10)",
                      border: "1px solid var(--border)",
                      gap: 6, fontSize: 12,
                    }}>
                    FY {y}
                    {canEdit && (
                      <button type="button" className="btn-icon"
                        title={`Remove FY ${y} column`}
                        onClick={() => removeHistoricalYear(y)}
                        style={{ padding: 2 }}>
                        <XIcon />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}

    </div>
  );
}